import React, { memo } from 'react';
import {
  Card,
  Button,
  InlineStack,
  BlockStack,
  Box,
  Tooltip,
  Text
} from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import LazyImage from './LazyImage';
import LocateBayButton from './LocateBayButton';
import ProductEtaRow from './ProductEtaRow';
import PrintLabelQuantityModal from './PrintLabelQuantityModal';
import { resolveLabelQuantity } from '../utils/labelQuantity';
import { tubClashChipLabel } from '../utils/poTubClashAudit';
import { isPlaceholderBarcode } from '../utils/barcodePlaceholders';
import { currentBarcodesForWrite, replacePrimaryBarcode } from '../utils/productBarcodes';
import { fetchLiveVariantBarcodes } from '../utils/productBarcodesApi';
import { formatBarcode, formatWeight } from '../utils/formatters';
// The Products design language (icons, field chrome) lives in one module so
// the card and the bulk-update panel cannot drift apart.
import {
  IconBarcode, IconWeight, IconBox, IconWarehouse, IconPin, IconTag, IconTrash, IconPrinter, IconAlert, IconCalendar, IconKit, IconLink,
  FieldLabel, fieldShell, fieldInput
} from './fieldIcons';
import { expiryBadge, expiryStatusColors, linkedExpiryStores } from './expiry/expiryDisplay';
import { resolveVariantColor } from '../utils/variantColor';
import { expandLocationShorthand, formatLocationInputLive, isShelfOnlyBinCode } from '../utils/warehouseLocationParser';
import { isPhaserProduct } from '../utils/productStockEdit';
import { productStatusChip } from '../utils/shopifyProductStatus';
import ShelfSlotPicker from './ShelfSlotPicker';
import {
  changeoverPending,
  engineActive,
  kitIdFor,
  kitKind,
  kitKindInfo,
  kitPartLocationsHover,
  kitPartLocationsText,
  kitRoot,
  kitStockBreakdown,
  kitStoreKeyFor,
  otherStoreKey,
  storeLabel,
  twinKitFor
} from './kitSplits/kitSplitsApi';
import { isKitEntry } from './productLinks/productLinksApi';
import './productLinks/productLinkChips.css';

const LOCATION_FIELDS = ['binLocation', 'overflow1', 'overflow2'];

/**
 * ProductCard Component
 * Memoized to prevent unnecessary re-renders (70% reduction)
 * 
 * Only re-renders when product data, selection, or editable data changes
 */
const BoxDimensionsModal = React.lazy(() => import('./BoxDimensionsModal'));
const ProductBarcodesEditor = React.lazy(() => import('./ProductBarcodesEditor'));

/**
 * Store pills in the brand colours (Rhys 2026-08-29): PHASER in Phaser red,
 * CCC in CCC yellow — the same colours as the storefronts, so the card reads
 * at a glance which shop owns the product. Polaris Badge tones cannot express
 * brand colours, hence a drawn pill.
 */
/** "900 GRAMS" reads like a spec sheet; the card shows "900 g" / "1.3 kg"
    (Rhys 2026-08-29 precision pass). Editing still takes grams. */
function formatWeightShort(grams) {
  const n = Number(grams) || 0;
  if (n <= 0) return null;
  if (n < 1000) return `${n} g`;
  return `${Math.round((n / 1000) * 100) / 100} kg`;
}

const STORE_PILL_STYLES = {
  'Phaser FPV': { background: 'var(--brand-phaser, #e8192e)', color: '#ffffff', label: 'PHASER' },
  'CCC': { background: '#b45309', color: '#ffffff', label: 'CCC' },
  'Porlex': { background: '#475569', color: '#ffffff' },
  'Redeemer': { background: '#6d28d9', color: '#ffffff' }
};

const STATUS_CHIP_COLORS = {
  info: { ink: 'var(--app-info-ink)', wash: 'var(--app-wash-info)' },
  warning: { ink: 'var(--app-warning-ink)', wash: 'var(--app-wash-warning)' },
  critical: { ink: 'var(--app-critical-ink)', wash: 'var(--app-wash-critical)' }
};

/** Shopify status chip (Unlisted / Draft / Archived); nothing for ACTIVE. */
function ProductStatusChip({ status }) {
  const chip = productStatusChip(status);
  if (!chip) return null;
  const colors = STATUS_CHIP_COLORS[chip.tone] || STATUS_CHIP_COLORS.info;
  return (
    <span
      data-testid="product-status-chip"
      title={chip.title}
      style={{
        display: 'inline-flex', alignItems: 'center',
        padding: '4px 10px', borderRadius: '7px',
        fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap', lineHeight: 1.2,
        border: `1px solid ${colors.ink}`, background: colors.wash, color: colors.ink
      }}
    >
      {chip.label}
    </span>
  );
}

function StorePill({ name, fallbackLabel }) {
  const cfg = STORE_PILL_STYLES[name] || {};
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '4px 10px', borderRadius: '7px',
      fontSize: '12px', fontWeight: 700, letterSpacing: '0.05em',
      textTransform: 'uppercase', whiteSpace: 'nowrap', lineHeight: 1.2,
      background: cfg.background || 'var(--app-surface-3)',
      color: cfg.color || 'var(--app-ink)'
    }}>
      {cfg.label || fallbackLabel || name}
    </span>
  );
}


/**
 * Food expiry on the card (openspec food-expiry-tracking): a quiet
 * "Tracks expiry" toggle for warehouse users, and on tracked cards a badge
 * "Exp 21 Nov 2026 · 5 left" (red expired / amber within 60 days / grey) or
 * "No expiry recorded", which opens the batches panel. Colours are theme
 * tokens so dark mode follows.
 */
function ExpiryCardControls({ product, productKey, canToggle, onToggle, onOpen }) {
  const [saving, setSaving] = React.useState(false);
  const hasSku = Boolean(product.sku && product.sku !== 'N/A' && product.store);
  const tracked = Boolean(product.expiryTracked);
  const badge = expiryBadge(product);
  if (!hasSku || (!canToggle && !badge)) return null;

  const colors = expiryStatusColors(badge?.status);
  const toggle = async (e) => {
    e.stopPropagation();
    if (saving) return;
    setSaving(true);
    try {
      await onToggle(product, productKey, !tracked);
    } finally {
      setSaving(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', minWidth: 0 }}>
      {badge && (
        <button
          type="button"
          title={badge.title}
          data-expiry-status={badge.status}
          onClick={(e) => { e.stopPropagation(); onOpen?.(product); }}
          disabled={!onOpen}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '3px 9px', borderRadius: '999px',
            border: `1px solid ${colors.border}`,
            background: colors.wash, color: colors.ink,
            fontSize: '12px', fontWeight: 700, lineHeight: 1.3, whiteSpace: 'nowrap',
            cursor: onOpen ? 'pointer' : 'default'
          }}
        >
          <IconCalendar />
          {badge.label}
        </button>
      )}
      {canToggle && (
        <label
          title={tracked
            ? 'This product tracks expiry dates: receiving asks for a date and it shows on the Expiry page. Untick to stop (recorded batches are kept).'
            : 'Track expiry dates for this product (food). Receiving a PO will ask for the expiry date.'}
          onClick={(e) => e.stopPropagation()}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap',
            color: tracked ? 'var(--app-ink-secondary)' : 'var(--app-ink-muted)',
            cursor: saving ? 'progress' : 'pointer', opacity: saving ? 0.6 : 1
          }}
        >
          <input
            type="checkbox"
            checked={tracked}
            disabled={saving}
            onChange={toggle}
            onClick={(e) => e.stopPropagation()}
            style={{ margin: 0, cursor: 'inherit' }}
          />
          Tracks expiry
        </label>
      )}
    </span>
  );
}

const ProductCard = memo(({
  product,
  productKey,
  isSelected,
  editable,
  editingFields,
  otherStoresLocations = [],
  onToggleSelection,
  onRemove,
  onUpdateField,
  onToggleEdit,
  onSave,
  onBarcodeEnter, // New: callback for Enter key in barcode field - triggers auto-save
  onSkuEnter, // Callback for Enter key in SKU field - triggers save + push to Shopify
  darkMode,
  recentlySaved = new Set(),
  isBulkUpdateTarget = false,
  getStoreBadgeTone,
  getStoreBadgeLabel,
  labelQuantity,
  onLabelQuantityChange,
  // A PO line can arrive in bulk (100 tubes) while we only label a handful,
  // so any high count loaded off a PO flashes until someone checks it
  // (Rhys 2026-09-04).
  needsLabelQuantityReview = false,
  onLabelQuantityReviewed = null,
  canEditEta = false,
  canEditStock = false, // CCC: editProductStock (Qoblex Total Stock)
  // Phaser: editLightspeedStock. Writes also need LIGHTSPEED_STOCK_WRITE_ENABLED
  // on the server; lightspeedStockWrites = { enabled, reason } from
  // /api/products/lightspeed-stock/capabilities, null until known.
  canEditLightspeedStock = false,
  lightspeedStockWrites = null,
  onSaveStock = null, // (product, productKey, targetQuantity, { expectedQuantity? }) => Promise
  onLoadStock = null, // Phaser: (product) => Promise<{ current }> live Lightspeed level
  tubClash = undefined, // PO putaway audit: { slotKey, vendor, partners[], existing[] } when this SKU's tub breaks the same-brand rule
  vendorRef = undefined, // {supplierSku, supplierName} | null once looked up
  canEditVendorRef = false,
  vendorRefKnownSupplier = null, // {supplierId, supplierName} from the loaded PO / existing mapping
  onLoadVendorRefSuppliers = null, // (product) => Promise<[{supplierId, supplierName}]>
  onSaveVendorRef = null,
  onPrintLabel = null, // (product, productKey) => opens the label quantity dialog
  onToggleNoBarcodeAck = null, // (product, productKey, next) => flag 'genuinely has no barcode'
  // Food expiry (warehouse module): the "Tracks expiry" toggle and the
  // next-expiry badge, which opens the batches panel.
  canTrackExpiry = false,
  onToggleExpiryTracked = null, // (product, productKey, next) => Promise
  onOpenExpiry = null, // (product) => opens ExpiryBatchesPanel
  // Kit splits (docs/memory/kit-splits.md): this product's entry in App's
  // kit index ({ asParent, asChild } | null) and the editor opener.
  kitLink = null,
  canEditKitSplit = false,
  onEditKitSplit = null, // (product, kit | null, options?) => opens KitSplitModal ({ mode: 'add-to-kit' } | { kind: 'mirror' })
  // Product links (docs/memory/product-links.md): this product's entry in
  // App's links index ({ link, complementedBy } | null), the editor opener and
  // the chip handler that opens a linked SKU in the grid.
  productLink = null,
  canEditProductLinks = false,
  onEditProductLinks = null, // (product, productLink | null) => opens ProductLinksModal
  onOpenLinkedProduct = null // (store, sku, entry) => scrolls to / loads that card
}) => {
  const editingWeight = editingFields[`${productKey}-weight`];
  const [showBoxDimensions, setShowBoxDimensions] = React.useState(false);
  const [showBarcodes, setShowBarcodes] = React.useState(false);
  // The label-quantity ask lays over THIS card's detail area (same pattern
  // as the dimensions editor) instead of a centred app modal.
  const [labelDialogOpen, setLabelDialogOpen] = React.useState(false);
  // Stock edit (CCC → Qoblex Total Stock, Phaser → Lightspeed level) — local
  // state, not the metafields save queue.
  const [editingStock, setEditingStock] = React.useState(false);
  const [stockDraft, setStockDraft] = React.useState('');
  const [stockSaving, setStockSaving] = React.useState(false);
  const [stockError, setStockError] = React.useState('');
  const skipStockBlurCommitRef = React.useRef(false);
  // Phaser edits the LIVE Lightspeed level, loaded when the editor opens; it
  // is also the expected-quantity guard the save sends (409 stale otherwise).
  const [stockLoading, setStockLoading] = React.useState(false);
  const [lightspeedCurrent, setLightspeedCurrent] = React.useState(null);
  const stockLoadTokenRef = React.useRef(0);
  const stockInputRef = React.useRef(null);
  // Enter / Escape close the SKU field themselves; the blur that follows must
  // not save a second time (Enter) or save a cancelled edit (Escape).
  const skipSkuBlurCommitRef = React.useRef(false);

  const editingBarcode = editingFields[`${productKey}-barcode`];
  const editingSku = editingFields[`${productKey}-sku`];
  const editingBinLocation = editingFields[`${productKey}-binLocation`];
  const editingOverflow1 = editingFields[`${productKey}-overflow1`];
  const editingOverflow2 = editingFields[`${productKey}-overflow2`];
  const editingDimensions = editingFields[`${productKey}-dimensions`];
  // The skip flag belongs to ONE edit. Removing a focused input does not
  // always fire blur, so a flag set by Enter / Escape could survive and
  // swallow the next edit's blur save (review 2026-10-30): reset it whenever
  // the field opens (and on focus, below).
  React.useEffect(() => {
    if (editingSku) skipSkuBlurCommitRef.current = false;
  }, [editingSku]);

  // Location quick entry (Rhys 2026-10-01): t5a1 types as T-05-A-1 and commits
  // as T-05-A-01; a shelf-only code (T-05-A) is never saved from this card -
  // the shelf slot picker asks where on the shelf first.
  const [shelfPick, setShelfPick] = React.useState(null); // { field, code }
  const shelfPickRef = React.useRef(null);
  shelfPickRef.current = shelfPick;
  // Close the field only once the expanded/picked value has reached the
  // editable prop, so App's close-time brand check reads the final code.
  const [pendingClose, setPendingClose] = React.useState(null); // { field, value }
  React.useEffect(() => {
    if (!pendingClose) return;
    if (editable[pendingClose.field] !== pendingClose.value) return;
    setPendingClose(null);
    if (editingFields[`${productKey}-${pendingClose.field}`]) onToggleEdit(productKey, pendingClose.field);
  }, [pendingClose, editable, editingFields, productKey, onToggleEdit]);

  /** A changed location that names a shelf but no slot, as { field, code }, else null. */
  const pendingShelfOnly = (edits) => {
    const field = LOCATION_FIELDS.find((name) => {
      if (!edits || edits[name] === undefined) return false;
      const code = expandLocationShorthand(edits[name]);
      return isShelfOnlyBinCode(code) && code !== expandLocationShorthand(product[name]);
    });
    return field ? { field, code: expandLocationShorthand(edits[field]) } : null;
  };

  /** Enter / Escape / blur on a location input: expand shorthand, force a slot, close. */
  const commitLocationField = (field) => {
    if (shelfPickRef.current) return; // the picker took focus; it decides
    const typed = editable[field];
    if (typed === undefined) { onToggleEdit(productKey, field); return; }
    const code = expandLocationShorthand(typed);
    const shelfOnly = pendingShelfOnly({ [field]: code });
    if (code !== typed) onUpdateField(productKey, field, code);
    if (shelfOnly) { setShelfPick(shelfOnly); return; }
    if (code !== typed) setPendingClose({ field, value: code });
    else onToggleEdit(productKey, field);
  };

  /** onSave, but a shelf-only location opens the slot picker instead. false = blocked. */
  const saveGuarded = (p, key, edits) => {
    const shelfOnly = pendingShelfOnly(edits);
    if (shelfOnly) { setShelfPick(shelfOnly); return false; }
    return onSave(p, key, edits);
  };

  // Check for missing fields
  const missingSku = !product.sku || product.sku === 'N/A';
  const missingWeight = !product.weight && !editable.weight;
  // A product ACKNOWLEDGED as having no barcode is not "missing" one - the
  // flag lives app-side (barcode_acks) so Shopify's barcode field stays
  // empty and Google feeds stay clean (Rhys 2026-08-29).
  const displayBarcode = editable.barcode !== undefined ? editable.barcode : product.barcode;
  const packingNote = typeof product.packingNotes === 'string' ? product.packingNotes.trim() : '';
  const missingBarcode = (!displayBarcode || isPlaceholderBarcode(displayBarcode)) && !product.noBarcodeAck;
  // Every barcode on the variant, typed (APP-06). The cached list only counts
  // while its first still equals the row's (live) barcode.
  const barcodeList = currentBarcodesForWrite(product, product.barcode ?? '');
  const extraBarcodes = Math.max(0, barcodeList.length - 1);
  // Sits on the narrow head line beside 'Barcode', so it stays short: '+2' with
  // extras, else the primary's type, else '+'. The full list is in its hover.
  const barcodeChip = barcodeList.length ? (extraBarcodes ? `+${extraBarcodes}` : (barcodeList[0].type || '+')) : '';
  const missingBinLocation = !product.binLocation && !editable.binLocation;

  // Check if other stores need syncing (using otherStoresLocations prop)
  // Only check if this is CCC (source of truth for Porlex/Redeemer)
  const isCCC = product.store === 'central_coast_coffee' ||
    product.store === 'central-coast-coffee.myshopify.com' ||
    product.storeDisplayName === 'Central Coast Coffee';
  const canEditQoblexStock = Boolean(canEditStock && isCCC && product.sku && typeof onSaveStock === 'function');
  // Phaser: the number talks to Lightspeed, whose level is what Shopify shows
  // as AVAILABLE (open-order units already deducted). Granted staff whose
  // writes are switched off keep a dead number with the reason on hover.
  const isPhaser = isPhaserProduct(product);
  const lightspeedGranted = Boolean(canEditLightspeedStock && isPhaser && product.sku
    && typeof onSaveStock === 'function' && typeof onLoadStock === 'function');
  const lightspeedWritesOn = lightspeedStockWrites?.enabled === true;
  const canEditLightspeedStockNow = lightspeedGranted && lightspeedWritesOn;
  // Kit stock engine (docs/memory/kit-stock-engine.md): an active assembled
  // kit's number is written from its parts, and set-stock refuses it (422),
  // so the number is not editable here; staff count the parts instead.
  const assembledKit = kitLink?.asParent && kitKind(kitLink.asParent) === 'assembled' && engineActive(kitLink.asParent)
    ? kitLink.asParent
    : null;
  // A twin (kind mirror) copies the other store's listing: its number is
  // written by the engine too, so it is set on the root, not here.
  const twinKit = kitLink?.asParent && kitKind(kitLink.asParent) === 'mirror' && engineActive(kitLink.asParent)
    ? kitLink.asParent
    : null;
  const twinRoot = twinKit ? kitRoot(twinKit) : null;
  const stockSystem = assembledKit || twinKit ? null : (canEditLightspeedStockNow ? 'lightspeed' : (canEditQoblexStock ? 'qoblex' : null));
  const stockEditable = stockSystem !== null;
  let stockHoverTitle;
  if (assembledKit) {
    const partSkus = (assembledKit.children || []).map((c) => c?.sku).filter(Boolean);
    const partList = partSkus.length > 1
      ? `${partSkus.slice(0, -1).join(', ')} and ${partSkus[partSkus.length - 1]}`
      : (partSkus[0] || 'the parts');
    stockHoverTitle = `Stock is worked out from the parts; count ${partList} instead`;
  } else if (twinKit) {
    stockHoverTitle = `Stock is copied from ${twinRoot?.sku || 'the root listing'} on ${storeLabel(twinRoot?.storeKey)}; set it there`;
  } else if (stockSystem === 'lightspeed') {
    stockHoverTitle = 'Click to set Lightspeed stock (what Shopify shows as available)';
  } else if (stockSystem === 'qoblex') {
    stockHoverTitle = 'Click to set Qoblex Total Stock';
  } else if (lightspeedGranted && lightspeedStockWrites && !lightspeedWritesOn) {
    stockHoverTitle = lightspeedStockWrites.reason || 'Lightspeed stock writes are off (LIGHTSPEED_STOCK_WRITE_ENABLED).';
  }
  // How an assembled kit's or a carrying part's number is made up, from the
  // engine's last run. A carrying part's edit takes the FULL number (shelf +
  // the kits), so its hover says so.
  const kitStock = kitStockBreakdown(kitLink, product);
  let kitStockTitle = null;
  if (kitStock?.kind === 'assembled') kitStockTitle = `${product.stockOnHand || 0} ${kitStock.text}`;
  else if (kitStock?.kind === 'mirror') kitStockTitle = `${product.stockOnHand || 0} ${kitStock.text}`;
  else if (kitStock?.kind === 'carrying') kitStockTitle = `${kitStock.text}${stockEditable ? '; type the full number' : ''}`;
  const stockNumberTitle = [kitStockTitle, stockHoverTitle].filter(Boolean).join('\n') || undefined;

  const openStockEdit = () => {
    if (!stockEditable || stockSaving) return;
    setStockError('');
    skipStockBlurCommitRef.current = false;
    setLightspeedCurrent(null);
    if (stockSystem === 'lightspeed') {
      // Edit the live Lightspeed level, not the overnight cache.
      const token = stockLoadTokenRef.current + 1;
      stockLoadTokenRef.current = token;
      setStockDraft('');
      setStockLoading(true);
      setEditingStock(true);
      let loading;
      try {
        loading = Promise.resolve(onLoadStock(product));
      } catch (err) {
        loading = Promise.reject(err);
      }
      loading
        .then((live) => {
          if (token !== stockLoadTokenRef.current) return;
          const current = Number(live?.current);
          if (!Number.isFinite(current)) throw new Error('Lightspeed did not return a stock level');
          setLightspeedCurrent(current);
          setStockDraft((draft) => (String(draft).trim() === '' ? String(current) : draft));
        })
        .catch((err) => {
          if (token !== stockLoadTokenRef.current) return;
          setStockError(err?.response?.data?.error || err?.message || 'Could not read Lightspeed stock');
        })
        .finally(() => {
          if (token === stockLoadTokenRef.current) setStockLoading(false);
        });
      return;
    }
    setStockDraft(String(product.stockOnHand ?? 0));
    setEditingStock(true);
  };

  // Once the live level lands, select it so typing replaces rather than appends.
  React.useEffect(() => {
    if (editingStock && !stockLoading && lightspeedCurrent !== null && stockInputRef.current) {
      stockInputRef.current.focus();
      stockInputRef.current.select();
    }
  }, [editingStock, stockLoading, lightspeedCurrent]);

  const cancelStockEdit = () => {
    if (stockSaving) return;
    stockLoadTokenRef.current += 1; // a late load must not reopen anything
    skipStockBlurCommitRef.current = true;
    setEditingStock(false);
    setStockLoading(false);
    setStockError('');
  };

  const commitStockEdit = async () => {
    if (!stockEditable || stockSaving || stockLoading) return;
    const lightspeed = stockSystem === 'lightspeed';
    const trimmed = String(stockDraft ?? '').trim();
    const target = Number(trimmed);
    // Lightspeed's level is "available", which is legitimately negative when
    // oversold, so only Qoblex (on hand) is held at zero or more.
    if (trimmed === '' || !Number.isInteger(target) || (!lightspeed && target < 0)) {
      setStockError(lightspeed ? 'Enter a whole number' : 'Enter a whole number ≥ 0');
      return;
    }
    if (lightspeed && lightspeedCurrent === null) {
      setStockError('Lightspeed stock did not load — press Escape and click the number again');
      return;
    }
    skipStockBlurCommitRef.current = true;
    setStockSaving(true);
    setStockError('');
    try {
      await onSaveStock(product, productKey, target, lightspeed ? { expectedQuantity: lightspeedCurrent } : {});
      setEditingStock(false);
    } catch (err) {
      skipStockBlurCommitRef.current = false;
      const liveNow = Number(err?.response?.data?.current);
      if (lightspeed && err?.response?.status === 409 && Number.isFinite(liveNow)) {
        // Stock moved since the editor opened: show the live number and let
        // the user decide again against it.
        setLightspeedCurrent(liveNow);
        setStockDraft(String(liveNow));
        setStockError(`Lightspeed now shows ${liveNow} — edit again`);
      } else {
        setStockError(err?.response?.data?.error || err?.message || 'Failed to update stock');
      }
    } finally {
      setStockSaving(false);
    }
  };

  // Helper to normalize empty values (null, undefined, "", "Not set" all become null)
  const normalize = (value) => {
    // Case-insensitive, and trimmed: this matched only the exact string
    // "Not set", but the card renders "Not Set" — so an unlocated product
    // still counted as located and drew MAP + LOCATE buttons, with a
    // "Light Not Set light blue" tooltip (Rhys 2026-08-29). locationKey()
    // below already had this right.
    const text = String(value ?? '').trim();
    if (!text || /^not set$/i.test(text)) return null;
    return value;
  };

  // Helper to normalize dimension values (string or number) to a number, or null
  const normalizeDim = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const num = parseFloat(value);
    return Number.isFinite(num) ? num : null;
  };

  // Current dimension values (editable wins, otherwise product value; cm)
  const currentLength = editable.length !== undefined ? editable.length : product.length;
  const currentWidth = editable.width !== undefined ? editable.width : product.width;
  const currentHeight = editable.height !== undefined ? editable.height : product.height;
  const hasDimensions = normalizeDim(currentLength) !== null || normalizeDim(currentWidth) !== null || normalizeDim(currentHeight) !== null;
  const dimensionsChanged = (
    (editable.length !== undefined && normalizeDim(editable.length) !== normalizeDim(product.length)) ||
    (editable.width !== undefined && normalizeDim(editable.width) !== normalizeDim(product.width)) ||
    (editable.height !== undefined && normalizeDim(editable.height) !== normalizeDim(product.height))
  );

  // Helper to generate Shopify admin product/variant URL
  const getAdminUrl = (storeUrl, productId, variantId = null) => {
    // Ensure store URL doesn't have protocol
    const cleanStoreUrl = storeUrl.replace(/^https?:\/\//, '');
    const storeName = cleanStoreUrl.split('.')[0]; // e.g. "phaser-fpv"

    // Extract pure numeric ID
    const getNumericId = (id) => {
      if (!id) return null;
      const idStr = String(id);
      if (idStr.includes('_')) return idStr.split('_').pop();
      if (idStr.includes('/')) return idStr.split('/').pop();
      return idStr;
    };

    const cleanProductId = getNumericId(productId);
    const cleanVariantId = getNumericId(variantId);

    // Modern Shopify Admin URL format
    // If we have both distinct IDs, link to the specific variant
    if (cleanVariantId && cleanProductId && cleanProductId !== cleanVariantId) {
      return `https://admin.shopify.com/store/${storeName}/products/${cleanProductId}/variants/${cleanVariantId}`;
    }
    // If we have a product ID (even if it equals variant ID due to data issues), use it
    if (cleanProductId) {
      return `https://admin.shopify.com/store/${storeName}/products/${cleanProductId}`;
    }

    // Fallback to search if IDs are missing
    return `https://admin.shopify.com/store/${storeName}/products?query=${encodeURIComponent(product.sku)}`;
  };

  // Map store display names to their Shopify store URLs
  const getStoreUrl = (storeDisplayName) => {
    const storeMap = {
      'Central Coast Coffee': 'central-coast-coffee.myshopify.com',
      'Porlex': 'porlex-australia.myshopify.com',
      'Redeemer': 'ace176-c7.myshopify.com',
      'Phaser FPV': 'phaser-fpv.myshopify.com'
    };
    return storeMap[storeDisplayName] || storeDisplayName;
  };

  // Get current values (use editable if exists, otherwise use product value)
  const displayBinLocation = editable.binLocation !== undefined ? editable.binLocation : product.binLocation;
  const currentBinLocation = normalize(displayBinLocation);
  const currentOverflow1 = normalize(editable.overflow1 !== undefined ? editable.overflow1 : product.overflow1);
  const currentOverflow2 = normalize(editable.overflow2 !== undefined ? editable.overflow2 : product.overflow2);
  const binLocationHistory = Array.isArray(product.binLocationHistory)
    ? product.binLocationHistory.filter(Boolean).slice(0, 10)
    : [];

  // Firestore-side W/H value — shown in the tooltip so a wiped Shopify
  // metafield (e.g. bad CSV import) can be spotted and restored by hand.
  const locationKey = (value) => {
    const text = String(value || '').trim();
    if (!text || /^not set$/i.test(text)) return '';
    return text.toUpperCase();
  };
  const dbBinLocation = locationKey(product.dbBinLocation) ? String(product.dbBinLocation).trim() : '';
  const dbBinLocationDiffers = Boolean(dbBinLocation) && locationKey(dbBinLocation) !== locationKey(displayBinLocation);

  const formatHistoryDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-AU', {
      timeZone: 'Australia/Sydney',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getChangedByLabel = (value) => {
    const text = String(value || '').trim();
    if (!text || text === 'unknown') return '';
    return text.includes('@') ? text.split('@')[0] : text;
  };

  const renderWarehouseLocationTooltip = () => (
    <div style={{ padding: '1px', minWidth: '220px', maxWidth: '320px' }}>
      <Text as="p" variant="bodyXs">W/H = Primary Warehouse Location (click to edit)</Text>

      {dbBinLocation && (
        <div style={{ marginTop: '8px' }}>
          <Text as="p" variant="bodyXs" fontWeight="bold">
            {product.dbBinLocationSource === 'archived'
              ? `Archived copy (Firestore): ${dbBinLocation}`
              : `Database (Firestore): ${dbBinLocation}`}
          </Text>
          {dbBinLocationDiffers && (
            <div style={{ color: 'var(--app-critical-ink)' }}>
              <Text as="p" variant="bodyXs">Differs from live Shopify — click W/H to restore</Text>
            </div>
          )}
        </div>
      )}

      {binLocationHistory.length > 0 && (
        <>
          <div style={{ marginTop: '8px' }}>
            <Text as="p" variant="bodyXs" fontWeight="bold">Previous: {binLocationHistory[0].from || 'Unknown'}</Text>
          </div>
          <div style={{ marginTop: '6px' }}>
            <Text as="p" variant="bodyXs" fontWeight="bold">Previous locations:</Text>
            {binLocationHistory.map((entry, idx) => {
              const dateLabel = formatHistoryDate(entry.changedAt);
              const byLabel = getChangedByLabel(entry.changedBy);
              return (
                <Text key={idx} as="p" variant="bodyXs">
                  - {entry.from || 'Unknown'} to {entry.to || 'Cleared'}{dateLabel ? ` (${dateLabel})` : ''}{byLabel ? ` by ${byLabel}` : ''}
                </Text>
              );
            })}
          </div>
        </>
      )}

      {otherStoresLocations && otherStoresLocations.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <Text as="p" variant="bodyXs" fontWeight="bold">Warehouse location at other stores:</Text>
          {otherStoresLocations.map((storeData, idx) => (
            <Text key={idx} as="p" variant="bodyXs">
              - {storeData.store}: {storeData.binLocation}
            </Text>
          ))}
        </div>
      )}
    </div>
  );


  // Check if there are unsaved edits
  const hasUnsavedEdits = (
    (editable.weight !== undefined && editable.weight !== product.weight) ||
    (editable.barcode !== undefined && editable.barcode !== product.barcode) ||
    (editable.binLocation !== undefined && normalize(editable.binLocation) !== normalize(product.binLocation)) ||
    (editable.overflow1 !== undefined && normalize(editable.overflow1) !== normalize(product.overflow1)) ||
    (editable.overflow2 !== undefined && normalize(editable.overflow2) !== normalize(product.overflow2)) ||
    dimensionsChanged
  );

  // Check if other stores have different values (compare to current values on screen)
  let syncDetails = [];
  const otherStoresNeedSync = isCCC &&
    otherStoresLocations &&
    otherStoresLocations.length > 0 &&
    otherStoresLocations.some(storeData => {
      const binMismatch = normalize(storeData.binLocation) !== currentBinLocation;
      const of1Mismatch = normalize(storeData.overflow1) !== currentOverflow1;
      const of2Mismatch = normalize(storeData.overflow2) !== currentOverflow2;

      syncDetails.push({
        store: storeData.store,
        binLocation: {
          store: storeData.binLocation,
          normalized: normalize(storeData.binLocation),
          current: currentBinLocation,
          mismatch: binMismatch
        },
        overflow1: {
          store: storeData.overflow1,
          normalized: normalize(storeData.overflow1),
          current: currentOverflow1,
          mismatch: of1Mismatch
        },
        overflow2: {
          store: storeData.overflow2,
          normalized: normalize(storeData.overflow2),
          current: currentOverflow2,
          mismatch: of2Mismatch
        }
      });

      return binMismatch || of1Mismatch || of2Mismatch;
    });

  // Individual field sync checks
  const binLocationNeedsSync = isCCC &&
    otherStoresLocations &&
    otherStoresLocations.length > 0 &&
    otherStoresLocations.some(storeData => normalize(storeData.binLocation) !== currentBinLocation);

  const overflow1NeedsSync = isCCC &&
    otherStoresLocations &&
    otherStoresLocations.length > 0 &&
    otherStoresLocations.some(storeData => normalize(storeData.overflow1) !== currentOverflow1);

  const overflow2NeedsSync = isCCC &&
    otherStoresLocations &&
    otherStoresLocations.length > 0 &&
    otherStoresLocations.some(storeData => normalize(storeData.overflow2) !== currentOverflow2);



  // Show green + save button if: there are unsaved edits OR stores don't match
  const hasChanges = hasUnsavedEdits || otherStoresNeedSync;

  // Card border carries ONE meaning: critically incomplete. It is never
  // repainted for editing/dirty/selected states - a green border here hid
  // the red warning and read as "complete" (Rhys 2026-08-29). Dirty state
  // lives in the footer beside Save; editing is the blue input alone.
  let borderClass = '';
  if (product.noSku) {
    borderClass = 'product-card-warning'; // Orange for missing SKU
  } else if (missingSku || missingWeight || missingBarcode) {
    borderClass = 'product-card-error';
  }

  const showSaveAction = hasChanges && !recentlySaved.has(productKey);

  // A bulk line loaded off a PO: flash the whole card until the count is
  // checked, so nobody prints 100 labels for tubes we sell individually.
  const showLabelQtyFlag = needsLabelQuantityReview && isSelected;

  return (
    <div className={[borderClass, showLabelQtyFlag ? 'product-card-qty-review' : ''].filter(Boolean).join(' ')}
      data-product-key={productKey}
      data-label-qty-review={showLabelQtyFlag ? 'true' : undefined}>
      {/* padding="100" (4px): Polaris' default 16px wasted a lot of screen on
          a wall of dense cards (Rhys 2026-08-29). */}
      <Card padding="100">
        <div
          style={{
            padding: '8px',
            position: 'relative'
          }}
        >
          <BlockStack gap="050">
            {showLabelQtyFlag && (
              <button
                type="button"
                className="qty-review-flag"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onLabelQuantityReviewed) onLabelQuantityReviewed(productKey);
                }}
                title="Click once you've checked how many labels this line really needs"
              >
                <span className="qty-review-flag__box">
                  {resolveLabelQuantity(labelQuantity)}
                </span>
                <span>CHECK QTY BEFORE PRINT</span>
                <span className="qty-review-flag__hint">tap when checked</span>
              </button>
            )}
            {/* Title with Checkbox */}
            {/* The gutter for the absolute trash button lives on the title
                column below, NOT here: Polaris InlineStack renders its own
                div and drops unknown props, so the `style` this used to carry
                never applied and the title ran under the icon. */}
            <InlineStack gap="100" wrap={false} blockAlign="start">
              {/* The selection checkbox rides the image's top-left corner
                  (Rhys 2026-08-29) — it floated loosely in the card corner
                  once the image grew. It only sits inline here when the
                  product has no image to carry it. Still 20px: warehouse
                  staff hit these on a touchscreen with gloves on. */}
              {!product.imageUrl && (
                <input
                  type="checkbox"
                  checked={isSelected || false}
                  onChange={() => onToggleSelection(productKey)}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    marginTop: '1px',
                    flexShrink: 0,
                    accentColor: '#2563eb'
                  }}
                />
              )}
              {/* Header: image beside the title, then variant and SKU as one
                  identity line, then the store badges with the restock ETA.
                  The old card stacked title, variant, ETA, image, badges, SKU
                  and stock as seven separate bands (Rhys 2026-08-29). */}
              <div style={{ display: 'flex', gap: '10px', flex: 1, minWidth: 0 }}>
              {/* Image - Increased to 110px for better space utilization */}
              {product.imageUrl && (
                <div style={{
                  width: '116px',
                  height: '116px',
                  flexShrink: 0,
                  overflow: 'hidden',
                  borderRadius: '4px',
                  backgroundColor: 'var(--app-surface-2)',
                  position: 'relative'
                }}>
                  {/* Tucked onto the image corner. The white halo keeps the
                      20px glove-target legible over any product photo. */}
                  <input
                    type="checkbox"
                    checked={isSelected || false}
                    onChange={() => onToggleSelection(productKey)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '6px',
                      left: '6px',
                      zIndex: 2,
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#2563eb',
                      borderRadius: '4px',
                      boxShadow: '0 0 0 2px rgba(255,255,255,0.9), 0 1px 3px rgba(0,0,0,0.25)'
                    }}
                  />
                  <LazyImage
                    src={product.imageUrl}
                    alt={product.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </div>
              )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: 1, minWidth: 0 }}>
                {/* The trash rides the title row as a real flex item. It used
                    to be absolutely positioned in the card corner, with its
                    gutter declared as a `style` prop on a Polaris InlineStack
                    — which renders its own div and drops unknown props, so the
                    gutter never existed and the icon sat on top of the title,
                    then on the right-aligned ETA (Rhys 2026-08-29). In normal
                    flow it cannot collide with either, and the ETA below is
                    free to hug the card's right edge. */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                  {/* The title links to the customer-facing product page when
                      the server could build one (Rhys 2026-08-29). productUrl
                      comes from /api/products/search via the shared
                      buildStorefrontProductUrl, so the storefront domains stay
                      in one file; it is null for Porlex/Redeemer, whose public
                      domains we don't hold, and the title renders plain. */}
                  {(() => {
                    const titleStyle = {
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: '1.25',
                      fontSize: '17px',
                      fontWeight: '700',
                      minHeight: '43px',
                      flex: 1,
                      minWidth: 0
                    };
                    if (!product.productUrl) {
                      return <div style={titleStyle}>{product.title}</div>;
                    }
                    return (
                      <a
                        href={product.productUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title={`Open ${product.title} on the website`}
                        style={{
                          ...titleStyle,
                          color: 'inherit',
                          textDecoration: 'none',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                      >
                        {product.title}
                      </a>
                    );
                  })()}
                  <button
                    type="button"
                    aria-label="Remove this product from the list"
                    title="Remove from the list"
                    onClick={(e) => { e.stopPropagation(); onRemove(productKey); }}
                    style={{
                      flexShrink: 0, display: 'flex', alignItems: 'center',
                      padding: '3px', marginTop: '-2px', borderRadius: '6px',
                      border: '1px solid transparent', background: 'transparent',
                      color: darkMode ? '#fca5a5' : '#b42318', cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = darkMode ? 'rgba(248,113,113,0.15)' : '#fef3f2';
                      e.currentTarget.style.borderColor = darkMode ? 'rgba(248,113,113,0.35)' : '#fecdca';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = 'transparent';
                    }}
                  >
                    <IconTrash size={20} />
                  </button>
                </div>
                  {/* Variant and SKU read as one identity line. */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', minWidth: 0,
                    fontSize: '14px', color: 'var(--app-ink-secondary)'
                  }}>
                  {product.variantTitle && product.variantTitle !== 'Default Title' && (() => {
                    // Colour variants pop: staff pick props/parts by colour, so
                    // "Red" reads as red with a swatch dot, larger than the old
                    // 11px grey line (Rhys 2026-08-24). Non-colour variants
                    // keep the quiet grey.
                    const variantColor = resolveVariantColor(product.variantTitle);
                    if (variantColor) {
                      return (
                        <div
                          style={{
                            fontSize: '13px',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            minWidth: 0,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: darkMode ? variantColor.darkText : variantColor.lightText
                          }}
                        >
                          <span style={{
                            width: '11px', height: '11px', borderRadius: '50%', flexShrink: 0,
                            background: variantColor.swatch,
                            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.25)'}`
                          }} />
                          {product.variantTitle}
                        </div>
                      );
                    }
                    return (
                      <div style={{
                        fontSize: '13px',
                        color: 'var(--app-ink-secondary)',
                        fontWeight: '500',
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {product.variantTitle}
                      </div>
                    );
                  })()}
                    {product.variantTitle && product.variantTitle !== 'Default Title' && (
                      <span aria-hidden style={{ color: 'var(--app-ink-muted)' }}>•</span>
                    )}
                {/* SKU - editable inline */}
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!editingSku) onToggleEdit(productKey, 'sku');
                  }}
                  style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    padding: '1px 4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: missingSku && !editingSku ? (darkMode ? '2px solid #f87171' : '2px solid #dc2626') :
                      '1px solid transparent',
                    borderRadius: '4px',
                    cursor: editingSku ? 'default' : 'pointer',
                    backgroundColor: editingSku ? 'transparent' :
                      missingSku ? (darkMode ? 'rgba(248, 113, 113, 0.15)' : '#fef2f2') :
                        'transparent',
                  }}
                >
                  {editingSku ? (
                    <>
                      <span style={{ color: 'var(--app-ink-secondary)', fontWeight: 600 }}>SKU</span>
                      <input
                        type="text"
                        value={editable.sku !== undefined ? editable.sku : (product.sku && product.sku !== 'N/A' ? product.sku : '')}
                        onChange={(e) => {
                          e.stopPropagation();
                          onUpdateField(productKey, 'sku', e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={() => { skipSkuBlurCommitRef.current = false; }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            skipSkuBlurCommitRef.current = true;
                            if (onSkuEnter) {
                              onSkuEnter(product, productKey, editable);
                            } else {
                              onToggleEdit(productKey, 'sku');
                            }
                          }
                          if (e.key === 'Escape') {
                            skipSkuBlurCommitRef.current = true;
                            onToggleEdit(productKey, 'sku');
                          }
                        }}
                        onBlur={() => {
                          // Clicking away saves exactly like Enter. Before this,
                          // a typed SKU was only pushed to Shopify on Enter -
                          // tabbing or clicking out left the card showing the
                          // new value with nothing saved (Rhys 2026-10-29).
                          if (skipSkuBlurCommitRef.current) {
                            skipSkuBlurCommitRef.current = false;
                            return;
                          }
                          const typedSku = editable.sku !== undefined ? String(editable.sku).trim() : '';
                          const currentSku = product.sku && product.sku !== 'N/A' ? String(product.sku).trim() : '';
                          if (typedSku && typedSku !== currentSku && onSkuEnter) {
                            onSkuEnter(product, productKey, editable);
                          } else {
                            onToggleEdit(productKey, 'sku');
                          }
                        }}
                        placeholder="Enter SKU"
                        autoFocus
                        style={{ ...fieldInput(darkMode), fontWeight: 600, width: '170px', flex: 'none' }}
                      />
                    </>
                  ) : (
                    <>
                      {product.sku && product.sku !== 'N/A'
                        ? product.sku
                        : <span style={{ color: darkMode ? '#fca5a5' : '#dc2626', fontWeight: 'bold', cursor: 'pointer' }}>click to set</span>}
                    </>
                  )}
                </div>
                  </div>
                  {/* Store badges and the customer-facing restock ETA. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
                {/* Store Badges */}
                <InlineStack gap="50" wrap={false}>
                  <a
                    href={getAdminUrl(product.store, product.parentId || product.product_id || product.productId || product.productIdLegacy, product.variantIdLegacy || product.variantId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{ textDecoration: 'none', cursor: 'pointer' }}
                  >
                    <StorePill name={product.storeDisplayName} fallbackLabel={getStoreBadgeLabel?.(product.storeDisplayName)} />
                  </a>
                  {/* Linked stores */}
                  {product.linkedStores && product.linkedStores.length > 1 &&
                    product.linkedStores
                      .filter(store => store !== product.storeDisplayName)
                      .map(linkedStore => {
                        // Try to find productId, variantId and storeUrl from otherStoresLocations
                        const storeData = otherStoresLocations?.find(loc => loc.store === linkedStore);
                        const linkedProductId = storeData?.productId; // Don't fallback to current product ID - different stores have different IDs
                        const linkedVariantId = storeData?.variantId; // Don't fallback - different stores have different variant IDs
                        const linkedStoreUrl = storeData?.storeUrl || getStoreUrl(linkedStore);

                        // If we have the product ID, link directly to variant (if available) or product. Otherwise, search by SKU
                        const adminUrl = linkedProductId
                          ? getAdminUrl(linkedStoreUrl, linkedProductId, linkedVariantId)
                          : `https://${linkedStoreUrl}/admin/products?query=${encodeURIComponent(product.sku)}`;

                        return (
                          <a
                            key={linkedStore}
                            href={adminUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ textDecoration: 'none', cursor: 'pointer' }}
                          >
                            <StorePill name={linkedStore} fallbackLabel={getStoreBadgeLabel?.(linkedStore)} />
                          </a>
                        );
                      })
                  }
                  <ProductStatusChip status={product.status} />
                </InlineStack>
                  {/* Customer-facing restock ETA — up top so receiving staff
                      see it immediately; edits gated by setEtaDates. */}
                  <ProductEtaRow product={product} darkMode={darkMode} canEdit={canEditEta} />
                  <ExpiryCardControls
                    product={product}
                    productKey={productKey}
                    canToggle={canTrackExpiry && typeof onToggleExpiryTracked === 'function'}
                    onToggle={(p, key, next) => onToggleExpiryTracked(p, key, next, linkedExpiryStores(otherStoresLocations, p.store))}
                    onOpen={onOpenExpiry}
                  />
                  </div>
                </div>
              </div>
            </InlineStack>

            {/* Everything below the header lives in one relative wrapper so the
                dimensions editor can lay itself over the card's own detail
                area - staff edit where they were already looking, with the
                title and ETA still visible above (Rhys 2026-08-29). */}
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '0px', flex: 1 }}>
            {/* Stock and location: the two figures staff read from across the
                aisle, so they get size rather than a label-and-value row. */}
            <Box>
              <div style={{
                // Location (with its lamp/map actions beside the value) and
                // Stock, roughly 70/30 - the buttons belong to the location
                // they act on (Rhys 2026-08-29 finishing pass). Same 12px
                // edge rhythm as the details grid below.
                display: 'grid', gridTemplateColumns: '1fr 96px',
                alignItems: 'start', columnGap: '12px',
                padding: '4px 12px 4px'
              }}>
                <div style={{ gridColumn: 2, gridRow: 1, textAlign: 'center', borderLeft: '1px solid var(--app-border)', paddingLeft: '12px' }}>
                  <div style={{
                    fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em',
                    textTransform: 'uppercase', color: 'var(--app-ink-secondary)',
                    marginBottom: '2px'
                  }}>Stock</div>
                  {editingStock ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <input
                        ref={stockInputRef}
                        type="number"
                        min={stockSystem === 'lightspeed' ? undefined : 0}
                        step={1}
                        autoFocus
                        disabled={stockSaving}
                        value={stockDraft}
                        placeholder={stockLoading ? '…' : undefined}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => setStockDraft(e.target.value)}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitStockEdit();
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelStockEdit();
                          }
                        }}
                        onBlur={() => {
                          if (skipStockBlurCommitRef.current) return;
                          // Clicking away while Lightspeed is still loading
                          // means "never mind", not "save whatever is there".
                          if (stockLoading) { cancelStockEdit(); return; }
                          if (!stockSaving) commitStockEdit();
                        }}
                        title={`${stockSystem === 'lightspeed' ? 'Lightspeed stock (= Shopify available)' : 'Qoblex Total Stock'}${kitStock?.kind === 'carrying' ? `\n${kitStock.text}; type the full number` : ''}`}
                        style={{
                          width: '72px',
                          fontSize: '28px',
                          fontWeight: 700,
                          lineHeight: '32px',
                          fontVariantNumeric: 'tabular-nums',
                          textAlign: 'center',
                          border: '2px solid #16a34a',
                          borderRadius: '4px',
                          background: darkMode ? 'rgba(22, 163, 74, 0.15)' : '#f0fdf4',
                          color: 'inherit',
                          padding: '0 2px'
                        }}
                      />
                      {stockSystem === 'lightspeed' && !stockSaving && (
                        <div style={{ fontSize: '10px', color: 'var(--app-ink-secondary)', marginTop: '2px', lineHeight: 1.2 }}>
                          {stockLoading
                            ? 'Loading Lightspeed…'
                            : (lightspeedCurrent !== null ? `Lightspeed: ${lightspeedCurrent} (= Shopify available)` : null)}
                        </div>
                      )}
                      {stockSaving && (
                        <div style={{ fontSize: '10px', color: 'var(--app-ink-secondary)', marginTop: '2px' }}>Saving…</div>
                      )}
                      {stockError && (
                        <div style={{ fontSize: '10px', color: 'var(--app-critical-ink)', marginTop: '2px', lineHeight: 1.2 }}>{stockError}</div>
                      )}
                    </div>
                  ) : (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        openStockEdit();
                      }}
                      title={stockNumberTitle}
                      style={{
                        fontSize: '30px',
                        fontWeight: 700,
                        lineHeight: '32px',
                        fontVariantNumeric: 'tabular-nums',
                        cursor: stockEditable ? 'pointer' : (stockNumberTitle ? 'help' : 'default'),
                        borderRadius: '4px',
                        padding: stockEditable ? '0 2px' : 0,
                        outline: stockEditable ? '1px dashed transparent' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (stockEditable) e.currentTarget.style.outlineColor = 'var(--app-border)';
                      }}
                      onMouseLeave={(e) => {
                        if (stockEditable) e.currentTarget.style.outlineColor = 'transparent';
                      }}
                    >
                      {product.stockOnHand || 0}
                    </div>
                  )}
                  {(() => {
                    const onShelf = product.stockOnHand || 0;
                    const allocated = product.stockCommitted || 0;
                    // Shopify's own available figure when we have it; else derive.
                    const toSell = Number.isFinite(Number(product.stockAvailable))
                      ? Number(product.stockAvailable)
                      : Math.max(0, onShelf - allocated);
                    const hasAllocation = allocated > 0;
                    // A kit's breakdown ("= min(100, 100)") borrows the same
                    // two-line slot when nothing is allocated; with an
                    // allocation it rides in the hover only, so the strip
                    // height never changes.
                    const showKitLine = Boolean(kitStock) && !hasAllocation;
                    const allocationTitle = hasAllocation
                      ? `${onShelf} on the shelf · ${allocated} allocated to orders not yet sent · ${toSell} left to sell`
                      : null;
                    const blockTitle = [allocationTitle, kitStock?.text].filter(Boolean).join('\n') || undefined;
                    return (
                      <div
                        title={hasAllocation || showKitLine ? blockTitle : undefined}
                        style={{
                          position: 'relative',
                          fontSize: '12px', lineHeight: 1.35, fontWeight: 600,
                          whiteSpace: 'nowrap', cursor: hasAllocation || showKitLine ? 'help' : 'default'
                        }}
                      >
                        {/* ALWAYS occupies its two lines: when the sublines only
                            rendered on allocated products, those cards' strips
                            grew and everything below stepped down against their
                            neighbours (Rhys 2026-08-29). Hidden, not absent, so
                            every strip is the same height with no magic pixels. */}
                        <div style={{ color: 'var(--app-warning-ink)', visibility: hasAllocation ? 'visible' : 'hidden' }}>{allocated} allocated</div>
                        <div style={{ color: 'var(--app-ink-secondary)', visibility: hasAllocation ? 'visible' : 'hidden' }}>{toSell} to sell</div>
                        {showKitLine && (
                          <div
                            data-testid="kit-stock-breakdown"
                            style={{
                              position: 'absolute', inset: 0,
                              fontSize: '10.5px', fontWeight: 500, lineHeight: 1.25,
                              whiteSpace: 'normal', overflowWrap: 'anywhere', overflow: 'hidden',
                              color: 'var(--app-ink-muted)', fontVariantNumeric: 'tabular-nums'
                            }}
                          >
                            {kitStock.short}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div style={{ gridColumn: 1, gridRow: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em',
                    textTransform: 'uppercase', color: 'var(--app-ink-secondary)',
                    marginBottom: '2px'
                  }}>Location</div>
                  {assembledKit && (
                    <div style={{
                      fontSize: '10px', fontWeight: 500, lineHeight: '12px',
                      color: 'var(--app-ink-muted)', marginTop: '-2px', marginBottom: '2px'
                    }}>parts</div>
                  )}
                  {/* Value and its actions on one line - the lamp/map act on
                      THIS location, so they sit beside it. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              {/* An active assembled kit has no shelf: it is picked as its
                  parts, so the cell shows their locations and is read-only
                  (docs/memory/kit-stock-engine.md). */}
              {assembledKit ? (() => {
                const partsText = kitPartLocationsText(assembledKit);
                return (
                  <div
                    data-testid="kit-part-locations"
                    title={kitPartLocationsHover(assembledKit)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ minWidth: 0, padding: '2px 4px', minHeight: '24px', display: 'flex', alignItems: 'center', cursor: 'default' }}
                  >
                    {partsText ? (
                      <span style={{
                        fontWeight: 700, fontSize: '22px', lineHeight: '28px',
                        fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere'
                      }}>
                        {partsText.split(' + ').map((loc, i) => (
                          <React.Fragment key={`${i}|${loc}`}>
                            {i > 0 && ' + '}
                            <span style={{ whiteSpace: 'nowrap' }}>{loc}</span>
                          </React.Fragment>
                        ))}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--app-ink-muted)', fontWeight: 600, fontSize: '15px', lineHeight: '28px' }}>
                        not set
                      </span>
                    )}
                  </div>
                );
              })() : (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  if (!editingBinLocation) {
                    // "Click W/H to restore": when live Shopify is blank but the
                    // database still holds a location, pre-fill the editor with it
                    // so the user only has to hit Enter and save.
                    if (editable.binLocation === undefined && !locationKey(displayBinLocation) && dbBinLocation) {
                      onUpdateField(productKey, 'binLocation', dbBinLocation);
                    }
                    onToggleEdit(productKey, 'binLocation');
                  }
                }}
                style={{
                  flex: '0 1 auto',
                  minWidth: 0,
                  padding: '2px 4px',
                  fontSize: '12px',
                  minHeight: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  border: isBulkUpdateTarget ? '2px solid #16a34a' : // Green border for bulk update target (same as single update)
                    editingBinLocation ? '1px solid transparent' :
                      missingBinLocation ? '1px solid transparent' :
                        (editable.binLocation !== undefined && editable.binLocation !== product.binLocation) ? '2px solid #16a34a' :
                          binLocationNeedsSync ? '2px solid #16a34a' :
                            '1px solid transparent',
                  borderRadius: '4px',
                  cursor: editingBinLocation ? 'default' : 'pointer',
                  backgroundColor: isBulkUpdateTarget ? (darkMode ? 'rgba(22, 163, 74, 0.2)' : '#f0fdf4') : // Green background (same as single update)
                    editingBinLocation ? 'transparent' :
                      missingBinLocation ? 'transparent' :
                        (editable.binLocation !== undefined && editable.binLocation !== product.binLocation) ? (darkMode ? 'rgba(22, 163, 74, 0.2)' : '#f0fdf4') :
                          binLocationNeedsSync ? (darkMode ? 'rgba(22, 163, 74, 0.2)' : '#f0fdf4') :
                            'transparent'
                }}
              >
                {editingBinLocation ? (
                  <>
                    {binLocationNeedsSync && otherStoresLocations && otherStoresLocations.length > 0 ? (
                      <Tooltip
                        content={
                          <div style={{ padding: '4px', minWidth: '200px' }}>
                            <Text as="p" variant="bodyXs" fontWeight="bold">Current locations at other stores:</Text>
                            {otherStoresLocations.map((storeData, idx) => (
                              <Text key={idx} as="p" variant="bodyXs">
                                • {storeData.store}: {storeData.binLocation}
                              </Text>
                            ))}
                          </div>
                        }
                        preferredPosition="above"
                      >
                        <span style={{ cursor: 'help' }}></span>
                      </Tooltip>
                    ) : null}
                    
                    <input
                      type="text"
                      value={editable.binLocation !== undefined ? editable.binLocation : (product.binLocation || '')}
                      onChange={(e) => {
                        e.stopPropagation();
                        onUpdateField(productKey, 'binLocation', formatLocationInputLive(e.target.value));
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter' || e.key === 'Escape') {
                          e.preventDefault();
                          commitLocationField('binLocation');
                        }
                      }}
                      onBlur={() => commitLocationField('binLocation')}
                      onFocus={(e) => e.target.select()}
                      placeholder="W/H Location"
                      aria-label="Warehouse location"
                      autoFocus
                      autoComplete="off"
                      autoCapitalize="characters"
                      spellCheck={false}
                      // Same size and weight as the value it replaces, so the
                      // location never shrinks while editing (Rhys 2026-10-01).
                      style={{
                        ...fieldInput(darkMode),
                        flex: 'none',
                        width: '10.5ch',
                        maxWidth: '100%',
                        height: '34px',
                        padding: '0 6px',
                        boxSizing: 'border-box',
                        fontSize: '28px',
                        fontWeight: 800,
                        lineHeight: '30px',
                        fontVariantNumeric: 'tabular-nums',
                        background: 'var(--app-input-bg)',
                        color: 'var(--app-ink)'
                      }}
                    />
                  </>
                ) : (
                  <>
                    {binLocationNeedsSync && otherStoresLocations && otherStoresLocations.length > 0 ? (
                      <Tooltip
                        content={
                          <div style={{ padding: '4px', minWidth: '200px' }}>
                            <Text as="p" variant="bodyXs" fontWeight="bold">Current locations at other stores:</Text>
                            {otherStoresLocations.map((storeData, idx) => (
                              <Text key={idx} as="p" variant="bodyXs">
                                • {storeData.store}: {storeData.binLocation}
                              </Text>
                            ))}
                          </div>
                        }
                        preferredPosition="above"
                      >
                        <span style={{ cursor: 'help' }}></span>
                      </Tooltip>
                    ) : null}
                    
                    <Tooltip
                      content={renderWarehouseLocationTooltip()}
                      preferredPosition="above"
                    >
                      <span style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: '6px', minWidth: 0, overflow: 'hidden' }}>
                        {/* Shopify stores the empty location as the literal
                            string "Not Set", so the raw value is truthy and an
                            unlocated product used to read as plain grey text.
                            An unset location is the thing staff most need to
                            see on this card (Rhys 2026-08-29). */}
                        {currentBinLocation
                          ? <span style={{ fontWeight: 800, fontSize: '28px', lineHeight: '32px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentBinLocation}</span>
                          : (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px',
                              color: darkMode ? '#fca5a5' : '#dc2626', fontWeight: 700, fontSize: '20px', lineHeight: '32px'
                            }}>
                              Not set
                              <IconAlert />
                            </span>
                          )}
                      </span>
                    </Tooltip>
                  </>
                )}
              </div>
              )}
                  {!assembledKit && currentBinLocation && (
                    <LocateBayButton location={currentBinLocation} square />
                  )}
                  </div>
                  {/* PO putaway audit: this SKU's stored tub breaks the
                      same-brand rule. Advisory — the fix is editing the
                      location above, and the flag clears itself on save. */}
                  {tubClash && (
                    <Tooltip
                      content={
                        <div style={{ padding: '4px', maxWidth: '260px' }}>
                          <Text as="p" variant="bodyXs" fontWeight="bold">
                            Same-brand tub ({tubClash.vendor}) — change one location before putaway
                          </Text>
                          {tubClash.partners.length > 0 && (
                            <Text as="p" variant="bodyXs">
                              Also arriving into {tubClash.slotKey} from this PO: {tubClash.partners.join(', ')}
                            </Text>
                          )}
                          {tubClash.existing.length > 0 && (
                            <Text as="p" variant="bodyXs">
                              Already in {tubClash.slotKey}: {tubClash.existing.map((p) => `${p.sku}${p.title ? ` — ${p.title}` : ''}`).join('; ')}
                            </Text>
                          )}
                          <Text as="p" variant="bodyXs">
                            Two items from one brand in one tub causes mispicks.
                          </Text>
                        </div>
                      }
                      preferredPosition="above"
                    >
                      <div style={{
                        marginTop: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        maxWidth: '100%',
                        padding: '2px 8px',
                        borderRadius: '999px',
                        border: '1px solid var(--app-warning-ink)',
                        backgroundColor: 'var(--app-wash-warning)',
                        color: 'var(--app-warning-ink)',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'help',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        ⚠ {tubClashChipLabel(tubClash)}
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
            </Box>
            {/* Two columns, mirroring the 2026-08-29 design: what the product
                IS on the left (barcode, weight, size), and where it came from
                and where else it lives on the right. Every cell keeps its own
                click-to-edit behaviour. */}
            <div style={{ flex: 1, display: 'flex' }}>
              {/* 3x2, the 2026-08-29 design: what the product IS across the
                  top (barcode, weight, size), where it came from and where
                  else it lives underneath. Hairline separators are pseudo
                  elements so the cells' own state borders stay untouched. */}
              <div className="pc-fieldgrid">
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  if (!editingBarcode) onToggleEdit(productKey, 'barcode');
                }}
                style={{
                  ...fieldShell({
                    editing: editingBarcode,
                    dirty: editable.barcode !== undefined && editable.barcode !== product.barcode,
                    missing: missingBarcode,
                    darkMode
                  }),
                  flex: '1 1 55%'
                }}
              >
                {editingBarcode ? (
                  <>
                    <div className="pc-cellhead"><IconBarcode /><span>Barcode</span></div>
                    <div style={{ position: 'relative', display: 'flex', minWidth: 0 }}>
                      <input
                        type="text"
                        value={editable.barcode !== undefined ? editable.barcode : (isPlaceholderBarcode(product.barcode) ? '' : (product.barcode || ''))}
                        onChange={(e) => {
                          e.stopPropagation();
                          onUpdateField(productKey, 'barcode', e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            // Use the new onBarcodeEnter callback for auto-save workflow
                            if (onBarcodeEnter) {
                              onBarcodeEnter(product, productKey, editable);
                            } else {
                              onToggleEdit(productKey, 'barcode');
                            }
                          }
                          if (e.key === 'Escape') {
                            onToggleEdit(productKey, 'barcode');
                          }
                          if (e.key === 'Tab' && !e.shiftKey) {
                            // Tab saves (the value is already staged) and opens
                            // the next field along. Guarded: onToggleEdit
                            // TOGGLES, so an already-open target would close.
                            e.preventDefault();
                            onToggleEdit(productKey, 'barcode');
                            if (!editingWeight) onToggleEdit(productKey, 'weight');
                          }
                        }}
                        onBlur={(_e) => {
                          // Don't trigger blur if we're about to handle Enter
                          // Check if there's a value and we should auto-save
                          const barcodeValue = editable.barcode !== undefined ? editable.barcode : product.barcode;
                          if (barcodeValue && barcodeValue.trim() !== '' && !isPlaceholderBarcode(barcodeValue) && onBarcodeEnter) {
                            // Let the blur happen naturally but don't duplicate the save
                            onToggleEdit(productKey, 'barcode');
                          } else {
                            onToggleEdit(productKey, 'barcode');
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                        placeholder="Barcode"
                        autoFocus
                        style={{ ...fieldInput(darkMode), flex: 1 }}
                      />
                      {(!displayBarcode || isPlaceholderBarcode(displayBarcode)) && !product.noBarcodeAck && onToggleNoBarcodeAck && product.sku && (
                        <button
                          type="button"
                          title="This product genuinely has no barcode - stop flagging it. Nothing is written to Shopify: junk placeholder barcodes break Google feeds."
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onToggleNoBarcodeAck(product, productKey, true);
                            onToggleEdit(productKey, 'barcode');
                          }}
                          style={{
                            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40,
                            whiteSpace: 'nowrap', cursor: 'pointer',
                            fontSize: '12px', fontWeight: 700, padding: '5px 10px',
                            borderRadius: '7px', border: '1px solid var(--app-border-strong)',
                            color: 'var(--app-ink-secondary)', background: 'var(--app-surface)',
                            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.18)'
                          }}
                        >Mark as no barcode</button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="pc-cellhead">
                      <IconBarcode /><span>Barcode</span>
                      {/* Type of the primary + how many more (APP-06). Opens the
                          full list: add barcodes, set types, pick the primary. */}
                      {barcodeChip && product.sku && (
                        <button
                          type="button"
                          className="pc-barcode-chip"
                          onClick={(e) => { e.stopPropagation(); setShowBarcodes(true); }}
                          title={`${barcodeList.map((b) => `${b.value} (${b.type || 'Other'})`).join('\n')}\n\nAll barcodes and their types. Every one scans in picking, packing and stocktake.`}
                          aria-label="Edit all barcodes"
                          style={{
                            flexShrink: 0, cursor: 'pointer', padding: '0 6px', lineHeight: '16px',
                            fontSize: '11px', fontWeight: 700, borderRadius: '999px',
                            border: '1px solid var(--app-border-strong)', background: 'var(--app-surface)',
                            color: 'var(--app-ink-secondary)'
                          }}
                        >{barcodeChip}</button>
                      )}
                    </div>
                    <div className="pc-cellval">
<span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                      {formatBarcode(isPlaceholderBarcode(displayBarcode) ? '' : displayBarcode) || (
                        product.noBarcodeAck ? (
                          <span
                            title="Marked as genuinely having no barcode - nothing is written to Shopify, so Google feeds stay clean. Click the cross to bring the reminder back."
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--app-ink-muted)' }}
                          >
                            no barcode
                            {onToggleNoBarcodeAck && (
                              <span
                                role="button"
                                tabIndex={0}
                                aria-label="Un-mark no barcode"
                                onClick={(e) => { e.stopPropagation(); onToggleNoBarcodeAck(product, productKey, false); }}
                                style={{ cursor: 'pointer', opacity: 0.7, fontWeight: 700 }}
                              >✕</span>
                            )}
                          </span>
                        ) : (
                          <>
                            <span style={{ color: darkMode ? '#fca5a5' : '#dc2626', fontSize: '13px' }}>click to set</span>
                          </>
                        )
                      )}
                    </span>
                    </div>
                  </>
                )}
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  if (!editingWeight) onToggleEdit(productKey, 'weight');
                }}
                style={{
                  ...fieldShell({
                    editing: editingWeight,
                    dirty: editable.weight !== undefined && editable.weight !== product.weight,
                    missing: missingWeight,
                    darkMode
                  }),
                  flex: '1 1 45%'
                }}
              >
                {editingWeight ? (
                  <>
                    <div className="pc-cellhead"><IconWeight /><span>Weight</span></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="number"
                        className="pc-num"
                        inputMode="numeric"
                        value={editable.weight !== undefined ? editable.weight : (product.weight || '')}
                        onChange={(e) => {
                          e.stopPropagation();
                          onUpdateField(productKey, 'weight', e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') {
                            onToggleEdit(productKey, 'weight');
                          }
                          if (e.key === 'Escape') {
                            onToggleEdit(productKey, 'weight');
                          }
                          if (e.key === 'Tab' && !e.shiftKey) {
                            e.preventDefault();
                            onToggleEdit(productKey, 'weight');
                            if (!editingOverflow1) onToggleEdit(productKey, 'overflow1');
                          }
                        }}
                        onBlur={() => onToggleEdit(productKey, 'weight')}
                        onFocus={(e) => e.target.select()}
                        placeholder="Weight in grams"
                        autoFocus
                        style={{ ...fieldInput(darkMode), flex: 1 }}
                      />
                      <span style={{ fontSize: '12px', color: 'var(--app-ink-muted)', flexShrink: 0 }}>grams</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="pc-cellhead"><IconWeight /><span>Weight</span></div>
                    <div className="pc-cellval">
<span style={{
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      color: (!product.weight || product.weight === 0) && (editable.weight === undefined || editable.weight === product.weight)
                        ? (darkMode ? '#fca5a5' : '#dc2626')
                        : undefined
                    }}>
                      {formatWeightShort(editable.weight !== undefined ? editable.weight : product.weight)
                        || <span style={{ fontSize: '13px' }}>click to set</span>}
                    </span>
                    </div>
                  </>
                )}
              </div>
            {/* Dimensions L×W×H (cm) - product metafields custom.length/width/height, read by Starshipit.
                Click opens the colour-coded box popup (staff mixed up sides);
                the inline inputs remain for keyboard/legacy flows via editingFields. */}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  if (!editingDimensions && !showBoxDimensions) setShowBoxDimensions(true);
                }}
                onBlur={(e) => {
                  // Close edit mode only when focus leaves the whole row (allows tabbing L -> W -> H)
                  if (editingDimensions && !e.currentTarget.contains(e.relatedTarget)) {
                    onToggleEdit(productKey, 'dimensions');
                  }
                }}
                style={fieldShell({
                  editing: editingDimensions,
                  dirty: dimensionsChanged,
                  missing: false,
                  darkMode
                })}
              >
                {editingDimensions ? (
                  <>
                    <div className="pc-cellhead"><IconBox /><span>Dimensions</span></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {['length', 'width', 'height'].map((dimField, idx) => (
                      <React.Fragment key={dimField}>
                        {idx > 0 && <span style={{ color: 'var(--app-ink-muted)' }}>×</span>}
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={editable[dimField] !== undefined ? editable[dimField] : (product[dimField] ?? '')}
                          onChange={(e) => {
                            e.stopPropagation();
                            onUpdateField(productKey, dimField, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.target.select()}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter' || e.key === 'Escape') {
                              e.preventDefault();
                              onToggleEdit(productKey, 'dimensions');
                            }
                          }}
                          placeholder={dimField === 'length' ? 'L' : dimField === 'width' ? 'W' : 'H'}
                          autoFocus={dimField === 'length'}
                          style={{
                            ...fieldInput(darkMode),
                            flex: 'none', width: '54px',
                            padding: '5px 4px', textAlign: 'center'
                          }}
                        />
                      </React.Fragment>
                    ))}
                    <span style={{ color: 'var(--app-ink-muted)', fontSize: '11px' }}>cm</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="pc-cellhead"><IconBox /><span>Dimensions</span></div>
                    <div className="pc-cellval">
                    <Tooltip
                      content={<Text as="p" variant="bodyXs">L × W × H in cm — shipping dimensions used by Starshipit (click to edit)</Text>}
                      preferredPosition="above"
                    >
                      <span style={{ cursor: 'help' }}>
                        {hasDimensions
                          ? `${normalizeDim(currentLength) ?? '–'} × ${normalizeDim(currentWidth) ?? '–'} × ${normalizeDim(currentHeight) ?? '–'} cm`
                          : <span style={{ color: darkMode ? '#fca5a5' : '#dc2626', fontSize: '13px' }}>click to set</span>}
                      </span>
                    </Tooltip>
                    </div>
                  </>
                )}
              </div>

            {/* Vendor reference — the supplier's own code for this product
                (planner supplier SKU map; auto-learnt from bills, scannable
                here during receiving when the grid was loaded from a PO). */}
            {(vendorRef !== undefined || canEditVendorRef) && (
                <VendorRefRow
                  vendorRef={vendorRef}
                  canEdit={canEditVendorRef}
                  knownSupplier={vendorRefKnownSupplier}
                  loadSuppliers={onLoadVendorRefSuppliers ? () => onLoadVendorRefSuppliers(product) : null}
                  onSave={(value, pickedSupplier) => onSaveVendorRef?.(product, productKey, value, pickedSupplier)}
                  darkMode={darkMode}
                />
            )}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  if (!editingOverflow1) onToggleEdit(productKey, 'overflow1');
                }}
                style={{
                  ...fieldShell({
                    editing: editingOverflow1,
                    dirty: (editable.overflow1 !== undefined && normalize(editable.overflow1) !== normalize(product.overflow1)) || overflow1NeedsSync,
                    missing: false,
                    darkMode
                  }),
                  flex: 1
                }}
              >
                {editingOverflow1 ? (
                  <>
                    <div className="pc-cellhead"><IconWarehouse /><span>OF1</span></div>
                    <input
                      type="text"
                      value={editable.overflow1 !== undefined ? editable.overflow1 : (product.overflow1 || '')}
                      onChange={(e) => {
                        e.stopPropagation();
                        onUpdateField(productKey, 'overflow1', formatLocationInputLive(e.target.value));
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter' || e.key === 'Escape') {
                          e.preventDefault();
                          commitLocationField('overflow1');
                        }
                        if (e.key === 'Tab' && !e.shiftKey) {
                          e.preventDefault();
                          commitLocationField('overflow1');
                          onToggleEdit(productKey, 'overflow2');
                        }
                      }}
                      onBlur={() => commitLocationField('overflow1')}
                      onFocus={(e) => e.target.select()}
                      placeholder="Scan location"
                      autoFocus
                      style={{ ...fieldInput(darkMode), flex: 1 }}
                    />
                  </>
                ) : (
                  <>
                    <div className="pc-cellhead"><IconWarehouse /><span>OF1</span></div>
                    <div className="pc-cellval">
                    <Tooltip
                      content={
                        <div style={{ padding: '4px', minWidth: '200px' }}>
                          {otherStoresLocations && otherStoresLocations.length > 0 ? (
                            <>
                              <Text as="p" variant="bodyXs" fontWeight="bold">Overflow 1 at other stores:</Text>
                              {otherStoresLocations.map((storeData, idx) => (
                                <Text key={idx} as="p" variant="bodyXs">
                                  • {storeData.store}: {storeData.overflow1 || 'Not set'}
                                </Text>
                              ))}
                            </>
                          ) : (
                            <Text as="p" variant="bodyXs">OF1 = First Overflow Location (click to edit)</Text>
                          )}
                        </div>
                      }
                      preferredPosition="above"
                    >
                      <span style={{ cursor: 'help' }}>
                        {editable.overflow1 !== undefined
                          ? (editable.overflow1 || <span style={{ color: 'var(--app-ink-muted)', fontSize: '13px' }}>click to set</span>)
                          : (product.overflow1 || <span style={{ color: 'var(--app-ink-muted)', fontSize: '13px' }}>click to set</span>)
                        }
                      </span>
                    </Tooltip>
                    </div>
                  </>
                )}
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  if (!editingOverflow2) onToggleEdit(productKey, 'overflow2');
                }}
                style={{
                  ...fieldShell({
                    editing: editingOverflow2,
                    dirty: (editable.overflow2 !== undefined && normalize(editable.overflow2) !== normalize(product.overflow2)) || overflow2NeedsSync,
                    missing: false,
                    darkMode
                  }),
                  flex: 1
                }}
              >
                {editingOverflow2 ? (
                  <>
                    <div className="pc-cellhead"><IconWarehouse /><span>OF2</span></div>
                    <input
                      type="text"
                      value={editable.overflow2 !== undefined ? editable.overflow2 : (product.overflow2 || '')}
                      onChange={(e) => {
                        e.stopPropagation();
                        onUpdateField(productKey, 'overflow2', formatLocationInputLive(e.target.value));
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter' || e.key === 'Escape') {
                          e.preventDefault();
                          commitLocationField('overflow2');
                        }
                      }}
                      onBlur={() => commitLocationField('overflow2')}
                      onFocus={(e) => e.target.select()}
                      placeholder="Scan location"
                      autoFocus
                      style={{ ...fieldInput(darkMode), flex: 1 }}
                    />
                  </>
                ) : (
                  <>
                    <div className="pc-cellhead"><IconWarehouse /><span>OF2</span></div>
                    <div className="pc-cellval">
                    <Tooltip
                      content={
                        <div style={{ padding: '4px', minWidth: '200px' }}>
                          {otherStoresLocations && otherStoresLocations.length > 0 ? (
                            <>
                              <Text as="p" variant="bodyXs" fontWeight="bold">Overflow 2 at other stores:</Text>
                              {otherStoresLocations.map((storeData, idx) => (
                                <Text key={idx} as="p" variant="bodyXs">
                                  • {storeData.store}: {storeData.overflow2 || 'Not set'}
                                </Text>
                              ))}
                            </>
                          ) : (
                            <Text as="p" variant="bodyXs">OF2 = Second Overflow Location (click to edit)</Text>
                          )}
                        </div>
                      }
                      preferredPosition="above"
                    >
                      <span style={{ cursor: 'help' }}>
                        {editable.overflow2 !== undefined
                          ? (editable.overflow2 || <span style={{ color: 'var(--app-ink-muted)', fontSize: '13px' }}>click to set</span>)
                          : (product.overflow2 || <span style={{ color: 'var(--app-ink-muted)', fontSize: '13px' }}>click to set</span>)
                        }
                      </span>
                    </Tooltip>
                    </div>
                  </>
                )}
              </div>
              </div>
            </div>

            <KitSplitRow
              product={product}
              kitLink={kitLink}
              canEdit={canEditKitSplit && typeof onEditKitSplit === 'function'}
              onOpen={(...args) => onEditKitSplit?.(product, ...args)}
              onOpenLinked={onOpenLinkedProduct}
              darkMode={darkMode}
            />

            <ProductLinksRow
              product={product}
              productLink={productLink}
              canEdit={canEditProductLinks && typeof onEditProductLinks === 'function'}
              onEdit={() => onEditProductLinks?.(product, productLink)}
              onOpenLinked={onOpenLinkedProduct}
              darkMode={darkMode}
            />

            {/* Item Packing Notes (custom.item_packing_notes, product or variant
                level). The same note pickers see on the tablet and packers see
                in Ship. Read-only here: edit it in Shopify's product metafields. */}
            {packingNote && (
              <div className="pc-packing-note" role="note" title="Item Packing Notes (Shopify product metafield). Shown to pickers and packers.">
                <span className="pc-packing-note-label">Packing note</span>
                <span className="pc-packing-note-text">{packingNote}</span>
              </div>
            )}

            {labelDialogOpen && (
              <PrintLabelQuantityModal
                inline
                open
                productCount={1}
                // Only a selected card has a label count to print "as entered";
                // an unselected one offers 1 label or a custom number (Rhys 2026-09-03).
                enteredTotal={isSelected ? resolveLabelQuantity(labelQuantity) : null}
                darkMode={darkMode}
                onCancel={() => setLabelDialogOpen(false)}
                onConfirm={(plan) => {
                  setLabelDialogOpen(false);
                  onPrintLabel?.(product, productKey, plan);
                }}
              />
            )}

            {showBarcodes && (
              <React.Suspense fallback={null}>
              <ProductBarcodesEditor
                barcodes={editable.barcode !== undefined ? replacePrimaryBarcode(barcodeList, editable.barcode) : barcodeList}
                productTitle={product.title}
                darkMode={darkMode}
                onClose={() => setShowBarcodes(false)}
                // A half-typed primary is already in the seed: keep the cached one.
                loadLive={editable.barcode === undefined ? () => fetchLiveVariantBarcodes(product) : null}
                onSave={async (next, base) => {
                  // The list already carries any half-typed primary.
                  if (editable.barcode !== undefined) onUpdateField(productKey, 'barcode', undefined);
                  await onSave(product, productKey, { barcodes: next, barcodesBase: base }, { awaitCompletion: true });
                }}
              />
              </React.Suspense>
            )}

            {showBoxDimensions && (
              <React.Suspense fallback={null}>
              <BoxDimensionsModal
                inline
                initial={{
                  length: currentLength ?? '',
                  width: currentWidth ?? '',
                  height: currentHeight ?? ''
                }}
                productTitle={product.title}
                darkMode={darkMode}
                onClose={() => setShowBoxDimensions(false)}
                onSave={async (values) => {
                  onUpdateField(productKey, 'length', values.length);
                  onUpdateField(productKey, 'width', values.width);
                  onUpdateField(productKey, 'height', values.height);
                  // Persist immediately through the card's normal save path so
                  // the popup is one action: enter → save → done.
                  await saveGuarded(product, productKey, {
                    ...editable,
                    length: values.length,
                    width: values.width,
                    height: values.height
                  });
                }}
              />
              </React.Suspense>
            )}


            {/* Footer: one action, printing this product's label. The design
                had an "Edit details" link on the left too, but every field on
                this card is already click-to-edit, so it would go nowhere
                (Rhys 2026-08-29). */}
            {onPrintLabel && (
              <div style={{ marginTop: 'auto' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '14px',
                  position: 'relative',
                  paddingTop: '6px', marginTop: '4px',
                  borderTop: '1px solid var(--app-border)'
                }}>
                {/* Dirty state: a quiet indicator + Save, sharing the footer row
                    so the card never changes height (Rhys 2026-08-29). */}
                {showSaveAction && (
                  <span style={{ marginRight: 'auto', display: 'inline-flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', color: darkMode ? '#fcd34d' : '#b45309' }}>&#9679; Unsaved</span>
                    <Button size="slim" variant="primary" onClick={() => saveGuarded(product, productKey, editable)}>
                      Save changes
                    </Button>
                  </span>
                )}
                {/* How many labels this card prints - beside the button that uses it (Rhys 2026-08-29).
                    Hidden while the Save cluster shows: the centred label overlapped it at narrow widths. */}
                {isSelected && !showSaveAction && (
                  <div style={{ fontSize: '12px', position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, calc(-50% + 4px))' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FieldLabel icon={<IconTag />}>Labels</FieldLabel>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={labelQuantity !== undefined && labelQuantity !== null ? labelQuantity : 1}
                        onChange={(e) => {
                          if (onLabelQuantityChange) {
                            const val = e.target.value;
                            // Allow empty string
                            if (val === '') {
                              onLabelQuantityChange(productKey, '');
                              return;
                            }
                            const newValue = Math.max(1, parseInt(val) || 1);
                            onLabelQuantityChange(productKey, newValue);
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                          // 45px clipped 3-digit counts to "10(" once the
                          // spinner took its share (Rhys 2026-09-04).
                          width: '68px',
                          padding: '2px 4px 2px 6px',
                          border: showLabelQtyFlag
                            ? '2px solid #f59e0b'
                            : '1px solid var(--app-border-strong)',
                          borderRadius: '4px',
                          fontSize: '12px',
                          background: darkMode ? '#111827' : '#ffffff',
                          color: darkMode ? '#e5e7eb' : '#202223'
                        }}
                      />
                    </label>
                  </div>
                )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Never print stale data: pending edits are queued for
                      // save first, and printLabels overlays them on the
                      // label itself (Rhys 2026-08-29 - auto-save-on-print).
                      if (hasUnsavedEdits && !recentlySaved.has(productKey)) {
                        if (saveGuarded(product, productKey, editable) === false) return;
                      }
                      setLabelDialogOpen(true);
                    }}
                    className="pc-printlabel"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '7px',
                      padding: '7px 14px', borderRadius: '8px', cursor: 'pointer',
                      fontSize: '12px', fontWeight: 700, border: 'none',
                      background: '#0d9488', color: '#fff'
                    }}
                  >
                    <IconPrinter />
                    Print label
                  </button>
                </div>
              </div>
            )}

            </div>

            {/* No print footer on this card: Save still needs a home. */}
            {!onPrintLabel && showSaveAction && (
              <Box paddingBlockStart="200">
                <Button size="slim" variant="primary" onClick={() => saveGuarded(product, productKey, editable)}>
                  Save changes
                </Button>
              </Box>
            )}
          </BlockStack>
        </div>
      </Card>
      {shelfPick && (
        <ShelfSlotPicker
          shelfCode={shelfPick.code}
          onPick={(full) => {
            const { field } = shelfPick;
            setShelfPick(null);
            onUpdateField(productKey, field, full);
            setPendingClose({ field, value: full });
          }}
          onCancel={() => {
            // Cancel forgets the shelf-only edit: back to the saved location.
            const { field } = shelfPick;
            setShelfPick(null);
            onUpdateField(productKey, field, undefined);
            setPendingClose({ field, value: undefined });
          }}
        />
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memo - only re-render if these props changed
  return (
    prevProps.product === nextProps.product &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.editable === nextProps.editable &&
    prevProps.editingFields === nextProps.editingFields &&
    prevProps.isBulkUpdateTarget === nextProps.isBulkUpdateTarget &&
    prevProps.vendorRef === nextProps.vendorRef &&
    prevProps.canEditVendorRef === nextProps.canEditVendorRef &&
    // By value: the known-supplier object is rebuilt every render in App.js
    prevProps.vendorRefKnownSupplier?.supplierId === nextProps.vendorRefKnownSupplier?.supplierId &&
    prevProps.vendorRefKnownSupplier?.supplierName === nextProps.vendorRefKnownSupplier?.supplierName &&
    prevProps.canEditEta === nextProps.canEditEta &&
    prevProps.canEditStock === nextProps.canEditStock &&
    prevProps.canEditLightspeedStock === nextProps.canEditLightspeedStock &&
    // App holds one { enabled, reason } object per session, so identity is enough.
    prevProps.lightspeedStockWrites === nextProps.lightspeedStockWrites &&
    prevProps.canTrackExpiry === nextProps.canTrackExpiry &&
    // kitLink is the index entry itself, so identity holds until a refetch.
    prevProps.kitLink === nextProps.kitLink &&
    prevProps.canEditKitSplit === nextProps.canEditKitSplit &&
    // A card first rendered before App wired the opener must pick it up.
    Boolean(prevProps.onEditKitSplit) === Boolean(nextProps.onEditKitSplit) &&
    // productLink is the index entry itself (shared by a product's SKUs).
    prevProps.productLink === nextProps.productLink &&
    prevProps.canEditProductLinks === nextProps.canEditProductLinks &&
    Boolean(prevProps.onEditProductLinks) === Boolean(nextProps.onEditProductLinks) &&
    Boolean(prevProps.onOpenLinkedProduct) === Boolean(nextProps.onOpenLinkedProduct) &&
    // A failed save removes the key from recentlySaved with no other compared
    // prop changing - without this the Save button never came back.
    prevProps.recentlySaved === nextProps.recentlySaved &&
    // The inline label dialog reads labelQuantity; a stale bail-out made its
    // "Actual quantities" count disagree with what actually printed.
    prevProps.labelQuantity === nextProps.labelQuantity &&
    // Without this the flag kept flashing after it was acknowledged.
    prevProps.needsLabelQuantityReview === nextProps.needsLabelQuantityReview &&
    prevProps.darkMode === nextProps.darkMode
  );
});

ProductCard.displayName = 'ProductCard';

/**
 * Kits section (docs/memory/kit-splits.md, kit-stock-engine.md). One line per
 * kit link, so a product inside one kit that is also a kit itself (Lotus
 * LCBWK: part of the full set, broken into the singles) shows and edits both:
 *
 *   Kits                                              [+ Add ▾]
 *   [Assembled] from   [img LCMB ×1] [img LCBWK ×1]        [Edit]   own kit
 *   [Part of]          [img 7880556900071] Assembled       [Edit]   each parent
 *   [Twin]     copies  [img 24577 PHASER]                  [Edit]   twin link
 *
 * Each line leads with a kind badge (colour by kind), then the relation
 * word, then product chips (image + SKU) and a muted tail ("changeover",
 * "off", "error"). Chips open that SKU in the grid; each line's Edit pill
 * (or a click on the line) opens that line's kit. "+ Add" is a small menu:
 * "Make a kit" (when the product is not a kit yet), "Add to a kit" (this
 * product becomes a part of another kit, new or existing) and "Link
 * CCC / PHASER twin" (the same item on the other site shares this stock).
 * Only Products (warehouse) users get the pills; others see the lines, or
 * nothing when the product is in no kit.
 * Full width under the 3x2 field grid so the grid's separator maths stays put.
 */
const KIT_BADGES = {
  assembled: { text: 'Assembled', tone: 'info' },
  broken: { text: 'Broken', tone: 'warning' },
  hold: { text: 'Hold both', tone: 'neutral' },
  mirror: { text: 'Twin', tone: 'success' }
};

function KitBadge({ kind, text, title }) {
  const badge = KIT_BADGES[kind] || { text: text || kind, tone: 'neutral' };
  return (
    <span className={`pc-kitkind pc-kitkind--${badge.tone}`} title={title} data-kind={kind}>
      {text || badge.text}
    </span>
  );
}

/** A product chip on a card row: image + SKU (+ store tag for the other store). */
function ProductChip({ sku, label, name, imageUrl, isKit, storeTag, onClick, title, className = '' }) {
  const body = (
    <>
      {imageUrl
        ? <img className="pc-linkchip-thumb" src={imageUrl} alt="" loading="lazy" />
        : (isKit ? <IconKit /> : null)}
      <span className="pc-linkchip-sku">{label || sku}</span>
      {storeTag && <span className={`pc-linkchip-store pc-linkchip-store--${String(storeTag).toLowerCase()}`}>{storeTag}</span>}
    </>
  );
  const classes = `pc-linkchip${isKit ? ' is-kit' : ''}${className ? ` ${className}` : ''}`;
  // Read-only cards with nowhere to go get plain chips, no buttons.
  if (!onClick) {
    return <span className={classes} style={{ cursor: 'inherit' }} title={title || name || undefined}>{body}</span>;
  }
  return (
    <button
      type="button"
      className={classes}
      title={title || `Open ${sku} in the grid${name ? ` (${name})` : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {body}
    </button>
  );
}

/** The "+ Add" menu on the Kits head: which kind of link to start. */
function KitAddMenu({ options }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  if (!options.length) return null;
  return (
    <span className="pc-kitmenu-wrap" ref={wrapRef} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="pc-editpill pc-editpill--add"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Add a kit link"
        title="Make this a kit, add it to a kit, or link its twin on the other site"
        data-testid="kit-add"
        onClick={() => setOpen((v) => !v)}
      >
        + Add <span className="pc-editpill-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="pc-kitmenu" role="menu" data-testid="kit-add-menu">
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              role="menuitem"
              className="pc-kitmenu-item"
              data-testid={option.testId}
              aria-label={option.ariaLabel || option.label}
              title={option.hint}
              onClick={() => { setOpen(false); option.onClick(); }}
            >
              <span className="pc-kitmenu-label">{option.label}</span>
              <span className="pc-kitmenu-hint">{option.hint}</span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function KitSplitRow({ product, kitLink, canEdit, onOpen, onOpenLinked, darkMode }) {
  const hasSku = Boolean(product?.sku && product.sku !== 'N/A' && product.store);
  const asParent = kitLink?.asParent || null;
  const asChild = Array.isArray(kitLink?.asChild) ? kitLink.asChild.filter((link) => link?.kit) : [];
  if (!hasSku || (!asParent && !asChild.length && !canEdit)) return null;

  const linked = Boolean(asParent || asChild.length);
  const storeKey = kitStoreKeyFor({ product });
  const otherStore = otherStoreKey(storeKey);
  const otherLabel = otherStore ? storeLabel(otherStore) : '';
  const engineTail = (kit) => {
    const bits = [];
    if (changeoverPending(kit)) bits.push({ text: 'changeover', tone: 'warning' });
    if (kit.enabled === false) bits.push({ text: 'off', tone: 'muted' });
    else if (kit.engine?.lastError) bits.push({ text: 'error', tone: 'critical', title: kit.engine.lastError });
    return bits;
  };
  const lines = [];
  if (asParent) {
    const kind = kitKind(asParent);
    const isOff = asParent.enabled === false;
    const isChangeover = changeoverPending(asParent);
    if (kind === 'mirror') {
      const root = kitRoot(asParent);
      lines.push({
        key: 'own',
        role: 'own',
        kit: asParent,
        kind,
        head: 'copies',
        chips: root ? [{ sku: root.sku, label: root.sku, title: root.title, imageUrl: root.imageUrl, store: root.store, storeTag: storeLabel(root.storeKey) }] : [],
        tail: engineTail(asParent),
        muted: isOff,
        hover: `Twin link: ${product.sku} copies the stock of ${root?.sku || 'its root'} on ${storeLabel(root?.storeKey)}; a sale here takes one from the root.${isOff ? ' Off: the app is not writing stock.' : ''}`,
        pillTitle: `Edit the twin link (${product.sku})`
      });
    } else {
      const parts = (asParent.children || [])
        .map((c) => `${c.qty || 1} × ${c.sku}${c.title ? ` ${c.title}` : ''}${kind === 'broken' && c.notPurchasable ? ' (carries the kit stock)' : ''}`)
        .join(', ');
      const hover = kind === 'hold'
        ? `Opens into ${parts}. A job is raised when a part's stock drops below ${asParent.flagBelow ?? 1}${isOff ? ' (checks are off)' : ''}.`
        : `${kitKindInfo(kind).label}: ${parts}. ${kitKindInfo(kind).hint}`
          + (isChangeover ? ' Changeover job open: every kit on the shelf is opened first.' : '')
          + (isOff ? ' Off: the app is not writing stock.' : '');
      lines.push({
        key: 'own',
        role: 'own',
        kit: asParent,
        kind,
        head: kind === 'assembled' ? 'from' : kind === 'broken' ? 'into' : 'opens into',
        chips: (asParent.children || []).filter((c) => c?.sku)
          .map((c) => ({
            sku: c.sku,
            label: `${c.sku} ×${Number(c.qty) || 1}`,
            title: c.title,
            imageUrl: c.imageUrl || null,
            store: c.store || product.store,
            carries: kind === 'broken' && c.notPurchasable
          })),
        tail: engineTail(asParent),
        muted: isOff,
        hover,
        pillTitle: `Edit this kit (${product.sku})`
      });
    }
  }
  asChild.forEach((link) => {
    const kit = link.kit;
    const kind = kitKind(kit);
    const qty = Number(link.child?.qty) || 1;
    const name = `${kit.parentSku}${kit.parentTitle ? ` ${kit.parentTitle}` : ''}`;
    if (kind === 'mirror') {
      // This product is the root: the other store's listing copies it.
      const twinStore = kit.storeKey || kitStoreKeyFor({ kit });
      lines.push({
        key: `twin-${kitIdFor(kit) || kit.parentSku}`,
        role: 'part-of',
        kit,
        kind,
        head: 'copied by',
        chips: [{ sku: kit.parentSku, label: kit.parentSku, title: kit.parentTitle, imageUrl: kit.parentImageUrl || null, store: kit.store, storeTag: storeLabel(twinStore) }],
        tail: engineTail(kit),
        muted: kit.enabled === false,
        hover: `Twin link: ${kit.parentSku} on ${storeLabel(twinStore)} copies this product's stock; a sale there takes one from here.${kit.enabled === false ? ' Off: the app is not writing stock.' : ''}`,
        pillTitle: `Edit the twin link (${kit.parentSku})`
      });
      return;
    }
    const carrying = Boolean(!asParent && link.child?.notPurchasable && kind === 'broken');
    lines.push({
      key: `of-${kitIdFor(kit) || kit.parentSku}`,
      role: 'part-of',
      kit,
      kind: null,
      badgeText: carrying ? 'From kit' : 'Part of',
      head: '',
      chips: [{ sku: kit.parentSku, label: kit.parentSku, title: kit.parentTitle, imageUrl: kit.parentImageUrl || null, store: kit.store, isKit: true }],
      tail: [
        { text: [kitKindInfo(kind).short, qty > 1 && `×${qty}`].filter(Boolean).join(' · '), tone: 'muted' },
        ...engineTail(kit).filter((b) => b.text !== 'off' || true)
      ].filter((b) => b.text),
      muted: kit.enabled === false,
      hover: carrying
        ? `Comes from breaking ${name} (${qty} per kit); it cannot be bought, so it carries its stock: its number includes the kits on the shelf.`
        : `Inside ${name}: ${qty} per kit (${kitKindInfo(kind).label.toLowerCase()}).`,
      pillTitle: `Edit kit ${kit.parentSku}`
    });
  });

  const hasTwin = Boolean(twinKitFor(kitLink));
  const menuOptions = canEdit ? [
    !asParent && {
      key: 'make',
      testId: 'kit-make',
      ariaLabel: 'Make this a kit',
      label: 'Make a kit',
      hint: `${product.sku} is the kit: pick its parts and how it is stocked`,
      onClick: () => onOpen(null)
    },
    {
      key: 'add',
      testId: 'kit-add-to',
      ariaLabel: 'Add to a kit',
      label: 'Add to a kit',
      hint: `${product.sku} becomes a part of another kit, new or existing`,
      onClick: () => onOpen(null, { mode: 'add-to-kit' })
    },
    otherStore && !hasTwin && {
      key: 'twin',
      testId: 'kit-link-twin',
      ariaLabel: `Link ${otherLabel} twin`,
      label: `Link ${otherLabel} twin`,
      hint: `The same item listed on ${otherLabel}: one copies the other's stock`,
      onClick: () => onOpen(null, { kind: 'mirror' })
    }
  ].filter(Boolean) : [];
  const canAdd = canEdit && !linked;
  return (
    <div
      data-testid="kit-split-row"
      title={linked ? undefined : `Kit: link this product to its parts, as assembled from parts, broken for parts, or both held; or link its twin on the other site.${canEdit ? ' Opens the editor.' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        if (canAdd) onOpen(null);
      }}
      style={{
        ...fieldShell({ editing: false, dirty: false, missing: false, darkMode }),
        minHeight: 0,
        borderTop: '1px solid var(--app-border)',
        borderRadius: 0,
        cursor: canAdd ? 'pointer' : 'default'
      }}
    >
      <div className="pc-cellhead">
        <IconKit />
        {linked ? <span>Kits</span> : <span>Kits <span className="pc-rowempty">· none</span></span>}
        <KitAddMenu options={menuOptions} />
      </div>
      {lines.map((line) => (
        <div
          key={line.key}
          className={`pc-kitline${line.muted ? ' is-muted' : ''}`}
          data-testid="kit-line"
          data-kit-role={line.role}
          title={`${line.hover}${canEdit ? ' Click to edit.' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (canEdit) onOpen(line.kit);
          }}
          style={{ cursor: canEdit ? 'pointer' : 'default' }}
        >
          <span className="pc-kitline-lead">
            <KitBadge kind={line.kind || 'hold'} text={line.badgeText || undefined} title={line.kind ? kitKindInfo(line.kind).hint : undefined} />
            {line.head && <span className="pc-kitline-head">{line.head}</span>}
          </span>
          <span className="pc-linkchips pc-kitline-chips">
            {line.chips.map((chip) => (
              <ProductChip
                key={`${chip.store || ''}|${chip.sku}`}
                sku={chip.sku}
                label={chip.label}
                name={chip.title && chip.title !== chip.sku ? chip.title : ''}
                imageUrl={chip.imageUrl}
                isKit={chip.isKit}
                storeTag={chip.storeTag}
                className={chip.carries ? 'is-carrying' : ''}
                title={chip.carries ? `Open ${chip.sku} in the grid${chip.title ? ` (${chip.title})` : ''}. Cannot be bought: carries the kit stock.` : undefined}
                onClick={onOpenLinked ? () => onOpenLinked(chip.store || product.store, chip.sku, { sku: chip.sku, title: chip.title }) : null}
              />
            ))}
            {line.tail.map((bit, i) => (
              <span key={`${bit.text}-${i}`} className={`pc-kitline-tail pc-kitline-tail--${bit.tone}`} title={bit.title || undefined}>{bit.text}</span>
            ))}
          </span>
          {canEdit && (
            <RowEditPill
              adding={false}
              what={line.role === 'own' ? 'kit' : `kit ${line.kit.parentSku}`}
              title={line.pillTitle}
              onClick={() => onOpen(line.kit)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The right-aligned "Edit" / "Add" pill on a card row that opens an editor
 * (kit row, related / complements rows; productLinkChips.css). Its own click
 * never reaches the row, so the editor opens once.
 */
function RowEditPill({ adding, what, title, onClick }) {
  const verb = adding ? 'Add' : 'Edit';
  return (
    <button
      type="button"
      className="pc-editpill"
      aria-label={`${verb} ${what}`}
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {verb}
    </button>
  );
}

/**
 * Product links rows (docs/memory/product-links.md), under the kit row:
 * "Related" and "Complements" lines of product chips (image + SKU), plus
 * "Complemented by" when another product's Complementary list names this
 * one. A chip opens that SKU in the grid; the "Edit" / "Add" pill on the
 * first line (or the row's empty space) opens the editor for Products users.
 * The Related head reads "Related / Complements" while the card has no
 * complements, so both lists are known to live in that editor. Unlinked
 * cards show "Related / Complements · add" to Products users and nothing to
 * everyone else.
 */
function ProductLinksRow({ product, productLink, canEdit, onEdit, onOpenLinked, darkMode }) {
  const hasSku = Boolean(product?.sku && product.sku !== 'N/A' && product.store);
  if (!hasSku) return null;
  const link = productLink?.link || null;
  const listOf = (value) => (Array.isArray(value) ? value.filter((e) => e && e.sku) : []);
  const related = listOf(link?.related);
  const complementary = listOf(link?.complementary);
  const ownLinks = related.length > 0 || complementary.length > 0;
  const lines = [
    { key: 'related', label: complementary.length ? 'Related' : 'Related / Complements', entries: related,
      hover: 'Shopify "You may also like" (two-way).' },
    { key: 'complementary', label: 'Complements', entries: complementary,
      hover: 'Shopify "Pairs well with": this product lists these.' },
    { key: 'complementedBy', label: 'Complemented by', entries: listOf(productLink?.complementedBy),
      hover: 'Products whose "Pairs well with" lists this one.' }
  ].filter((line) => line.entries.length);
  // Editors always get a way in: the empty Related line leads when this
  // product lists nothing itself (it may still be complemented by others).
  if (canEdit && !ownLinks) {
    lines.unshift({ key: 'empty', label: 'Related / Complements', entries: [],
      hover: 'Related / complementary products shown on the Shopify product page.' });
  }
  if (!lines.length) return null;

  const rowStyle = {
    ...fieldShell({ editing: false, dirty: false, missing: false, darkMode }),
    minHeight: 0,
    borderTop: '1px solid var(--app-border)',
    borderRadius: 0,
    cursor: canEdit ? 'pointer' : 'default'
  };
  const onRowClick = (e) => {
    e.stopPropagation();
    if (canEdit) onEdit();
  };

  return lines.map((line, i) => (
    <div
      key={line.key}
      data-testid="product-links-row"
      data-links={line.key === 'empty' ? undefined : line.key}
      className={`pc-linksrow pc-linksrow--${line.key}`}
      title={`${line.hover}${canEdit ? ' Opens the editor.' : ''}`}
      style={rowStyle}
      onClick={onRowClick}
    >
      <div className="pc-cellhead">
        <IconLink />
        {line.entries.length
          ? <span>{line.label}</span>
          : <span>{line.label} <span className="pc-rowempty">· add</span></span>}
        {canEdit && i === 0 && (
          <RowEditPill
            adding={!ownLinks}
            what="related products"
            title="Open the related / complements editor"
            onClick={onEdit}
          />
        )}
      </div>
      {line.entries.length > 0 && (
        <div className="pc-cellval">
          <div className="pc-linkchips">
            {line.entries.map((entry) => {
              const kit = isKitEntry(entry);
              const name = entry.title && entry.title !== entry.sku ? ` (${entry.title})` : '';
              return (
                <button
                  key={`${entry.productId || ''}|${entry.sku}`}
                  type="button"
                  className={`pc-linkchip${kit ? ' is-kit' : ''}`}
                  title={`Open ${entry.sku} in the grid${name}${kit ? '. Linked by its kit.' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenLinked?.(product.store, entry.sku, entry);
                  }}
                >
                  {entry.imageUrl
                    ? <img className="pc-linkchip-thumb" src={entry.imageUrl} alt="" loading="lazy" />
                    : (kit ? <IconKit /> : null)}
                  <span className="pc-linkchip-sku">{entry.sku}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  ));
}

/**
 * Vendor reference row: the supplier's code for this product from the
 * planner supplier SKU map.
 *
 * Save semantics (Rhys 2026-08-24): a barcode SCAN auto-saves — the gun
 * types the whole code in a burst (and usually sends Enter) — while hand
 * typing shows an explicit ✓ Save button so staff know when it's stored.
 * Clicking away cancels instead of silently saving. When the grid has no
 * supplier context (not loaded from a PO, no existing mapping) the row
 * offers a supplier picker, so anyone with the products grid can set refs.
 */
const SCAN_BURST_MIN_CHARS = 6;
const SCAN_BURST_MAX_MS_PER_CHAR = 40; // >25 chars/sec = a scanner, not fingers
const SCAN_BURST_SETTLE_MS = 350;

function VendorRefRow({ vendorRef, canEdit, knownSupplier, loadSuppliers, onSave, darkMode }) {
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [draft, setDraft] = React.useState('');
  const [suppliers, setSuppliers] = React.useState(null); // null = not loaded yet
  const [pickedSupplierId, setPickedSupplierId] = React.useState('');
  const burstRef = React.useRef({ startTs: 0, timer: null });
  const value = vendorRef?.supplierSku || null;
  const needsPicker = !knownSupplier;

  React.useEffect(() => () => clearTimeout(burstRef.current.timer), []);

  const openEditor = () => {
    setDraft(value || '');
    setError('');
    setEditing(true);
    burstRef.current.startTs = 0;
    if (needsPicker && suppliers == null && loadSuppliers) {
      setSuppliers('loading');
      loadSuppliers()
        .then((list) => setSuppliers(list || []))
        .catch(() => { setSuppliers([]); setError('Supplier list failed to load'); });
    }
  };

  const supplierForSave = () => {
    if (!needsPicker) return null; // App falls back to the known context
    return (Array.isArray(suppliers) ? suppliers : [])
      .find((s) => s.supplierId === pickedSupplierId) || null;
  };

  const commit = async (nextValue) => {
    const trimmed = String(nextValue ?? draft).trim();
    if (!trimmed || (trimmed === value && !needsPicker)) { setEditing(false); return; }
    const picked = supplierForSave();
    if (needsPicker && !picked) { setError('Pick a supplier first'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave?.(trimmed, picked);
      setEditing(false);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDraftChange = (nextValue) => {
    setDraft(nextValue);
    const b = burstRef.current;
    const now = performance.now();
    // A >500ms pause starts a new burst window — otherwise a scan performed
    // after some earlier typing in the same editor never reads as a scan.
    if (!b.startTs || !nextValue || (b.lastTs && now - b.lastTs > 500)) b.startTs = now;
    b.lastTs = now;
    clearTimeout(b.timer);
    // A whole code arriving faster than fingers can type = scanner → auto-save
    // once the input goes quiet (covers guns configured without an Enter suffix).
    b.timer = setTimeout(() => {
      const chars = String(nextValue || '').trim().length;
      const elapsed = performance.now() - b.startTs - SCAN_BURST_SETTLE_MS;
      if (chars >= SCAN_BURST_MIN_CHARS && elapsed / chars < SCAN_BURST_MAX_MS_PER_CHAR) {
        if (!needsPicker || supplierForSave()) commit(nextValue);
      }
    }, SCAN_BURST_SETTLE_MS);
  };

  const cancel = () => {
    clearTimeout(burstRef.current.timer);
    setEditing(false);
    setError('');
  };

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        if (canEdit && !editing && !saving) openEditor();
      }}
      onBlur={(e) => {
        // Same contract as every other field: clicking away SAVES a real,
        // changed value and quietly cancels otherwise (empty draft, unchanged
        // value, or no supplier picked yet). Focus moves within the row
        // (input ↔ supplier picker) are ignored.
        if (!editing || saving || e.currentTarget.contains(e.relatedTarget)) return;
        const draftTrim = String(draft || '').trim();
        if (draftTrim && draftTrim !== (value || '') && (!needsPicker || pickedSupplierId)) commit();
        else cancel();
      }}
      title={canEdit
        ? `Supplier's own code for this product${vendorRef?.supplierName ? ` (${vendorRef.supplierName})` : ''} — scan the vendor barcode (auto-saves) or type it and press Save. Feeds bill matching in the planner.`
        : 'Supplier\'s own code (from the planner supplier SKU map).'}
      style={{
        ...fieldShell({ editing, dirty: false, missing: false, darkMode }),
        cursor: canEdit && !editing ? 'pointer' : 'default'
      }}
    >
      <div className="pc-cellhead"><IconTag /><span>Vendor ref</span></div>
      {editing ? (
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', minWidth: 0 }}>
          {needsPicker && (
            <select
              value={pickedSupplierId}
              disabled={saving || !Array.isArray(suppliers)}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => { setPickedSupplierId(e.target.value); setError(''); }}
              style={{
                maxWidth: '110px', fontSize: '11px', padding: '1px 2px',
                border: `1px solid ${darkMode ? '#555' : '#d1d5db'}`, borderRadius: '4px',
                background: darkMode ? '#1f2937' : '#fff', color: 'inherit'
              }}
            >
              <option value="">{suppliers === 'loading' ? 'Loading…' : 'Supplier…'}</option>
              {(Array.isArray(suppliers) ? suppliers : []).map((s) => (
                <option key={s.supplierId} value={s.supplierId}>{s.supplierName}</option>
              ))}
            </select>
          )}
          <input
            type="text"
            autoFocus
            value={draft}
            disabled={saving}
            placeholder="Scan code"
            // Select-all on focus: scanning over a prefilled ref must replace
            // it, not append to it.
            onFocus={(e) => e.target.select()}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleDraftChange(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') cancel();
            }}
            style={{ ...fieldInput(darkMode), flex: 1 }}
          />
        </div>
      ) : (
      <div className="pc-cellval">
      {value ? (
        <>
          {/* No supplier name beside the code (Rhys 2026-08-29) - the row
              tooltip already names the supplier. */}
          <span style={{
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0
          }}>{value}</span>
        </>
      ) : vendorRef === undefined ? (
        <span style={{ color: 'var(--app-ink-muted)', fontSize: '11px' }}>…</span>
      ) : (
        <span style={{ color: 'var(--app-ink-muted)', fontSize: '12px' }}>
          {canEdit ? 'click to set' : 'not set'}
        </span>
      )}
      </div>
      )}
      {error && <span style={{ color: 'var(--app-critical-ink)', fontSize: '10px' }}>{error}</span>}
    </div>
  );
}

export default ProductCard;
